import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Users, FileText, Mic, Package, Mail, BarChart3, Plus, Edit, Trash2, Eye, Download, X, CheckCircle, Shield, Zap, Globe, Send, Clock, AlertCircle, Menu, TrendingUp, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import BlogForm from '../components/BlogForm';
import ServiceForm from '../components/ServiceForm';
import PodcastForm from '../components/PodcastForm';
import EstimatorLeadsPanel from '../components/admin/EstimatorLeadsPanel';
import RankingsPanel from '../components/admin/RankingsPanel';
import AuditsPanel from '../components/admin/AuditsPanel';
// CommandCenter, Prospector, GSCDashboard, Toolkits removed in Feb 2026 cleanup, their tabs are no longer surfaced in the Admin.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  
  // Data states
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [downloadConsents, setDownloadConsents] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Form states
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showPodcastForm, setShowPodcastForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [dripStats, setDripStats] = useState(null);
  const [dripLoading, setDripLoading] = useState(false);
  const [emailPreviews, setEmailPreviews] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [selectedEmailKey, setSelectedEmailKey] = useState(null);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Conversion insights (sticky bar + Bili chips)
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsDays, setInsightsDays] = useState(30);

  // SEO Tools tab state
  const [seoStatus, setSeoStatus] = useState(null);
  const [seoSlug, setSeoSlug] = useState('');
  const [seoOptions, setSeoOptions] = useState([]);
  const [seoBusy, setSeoBusy] = useState(false);
  const [indexnowResult, setIndexnowResult] = useState(null);
  const [interlinkResult, setInterlinkResult] = useState(null);
  // GSC "Weekly Quick Wins"
  const [gsc, setGsc] = useState(null);
  const [gscLoading, setGscLoading] = useState(false);
  const [gscDays, setGscDays] = useState(28);
  // Wrong-topic query -> pages expansion (per-row). Map of { [query]: { loading, pages, error } }
  const [wtExpanded, setWtExpanded] = useState({});
  // Bing Webmaster
  const [bing, setBing] = useState(null);
  const [bingLoading, setBingLoading] = useState(false);
  // Blog images rebuild
  const [rebuildResult, setRebuildResult] = useState(null);
  // Reddit Q&A generator
  const [redditForm, setRedditForm] = useState({ title: '', subreddit: 'podcasting', url: '', selftext: '' });
  const [redditBusy, setRedditBusy] = useState(false);
  const [redditResult, setRedditResult] = useState(null);

  const handleSendTestEmail = useCallback(async () => {
    if (!selectedEmailKey) {
      toast.error('Pick a template first');
      return;
    }
    const recipient = (testEmailRecipient || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient)) {
      toast.error('Enter a valid email address');
      return;
    }
    const token = localStorage.getItem('adminToken');
    setSendingTestEmail(true);
    try {
      await axios.post(
        `${API}/email-previews/send-test`,
        { key: selectedEmailKey, recipient, recipient_name: 'Admin Test' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Test email sent to ${recipient}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to send';
      toast.error(typeof msg === 'string' ? msg : 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  }, [selectedEmailKey, testEmailRecipient]);

  const fetchEmailPreviews = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;
    setEmailLoading(true);
    try {
      const res = await axios.get(`${API}/email-previews`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmailPreviews(res.data);
      if (res.data.previews?.length) {
        setSelectedEmailKey(res.data.previews[0].key);
      }
    } catch (e) {
      console.error('Failed to load email previews:', e);
    } finally {
      setEmailLoading(false);
    }
  }, []);

  const fetchDripStats = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;
    setDripLoading(true);
    try {
      const res = await axios.get(`${API}/newsletter/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDripStats(res.data);
    } catch (e) {
      console.error('Failed to load welcome-sequence stats:', e);
    } finally {
      setDripLoading(false);
    }
  }, []);

  const [drafts, setDrafts] = useState({ count: 0, posts: [] });
  const fetchDrafts = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/blog/drafts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDrafts(res.data || { count: 0, posts: [] });
    } catch (e) {
      // silent, dashboard still works without it
    }
  }, []);

  const republishDraft = async (slug, force = false) => {
    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(`${API}/admin/blog/republish`, { slug, force }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Published /blog/${slug} and notified search engines`);
      fetchDrafts();
    } catch (e) {
      const detail = e?.response?.data?.detail || ('Publish failed: ' + e.message);
      if (e?.response?.status === 409 && !force && detail.startsWith('A live post already covers')) {
        if (window.confirm(`${detail}\n\nPublish anyway? Only do this if the topics are genuinely different.`)) {
          return republishDraft(slug, true);
        }
        return;
      }
      toast.error(detail);
    }
  };

  const [regenBusySlug, setRegenBusySlug] = useState(null);

  // ---- Long-LLM job polling ------------------------------------------------
  // The Regenerate, Title/Meta rewriter, Reddit generator and FAQ Inject
  // endpoints now return {job_id} in ~200ms. We poll every 3s so
  // Cloudflare's 30s edge timeout never fires. Any long admin action goes
  // through here.
  const pollJob = async (jobId, {
    everyMs = 3000,
    maxWaitMs = 20 * 60 * 1000, // 20 min ceiling
    onTick = null,
  } = {}) => {
    const started = Date.now();
    // Small initial delay — most jobs need at least ~2s of LLM time.
    await new Promise((r) => setTimeout(r, 1500));
    while (Date.now() - started < maxWaitMs) {
      try {
        const res = await axios.get(`${API}/admin/jobs/${jobId}`, { headers: authHeaders(), timeout: 10000 });
        const j = res.data || {};
        if (onTick) onTick(j);
        if (j.status === 'done')  return { ok: true,  result: j.result, elapsed: j.elapsed_seconds };
        if (j.status === 'error') return { ok: false, error: j.error, elapsed: j.elapsed_seconds };
      } catch (e) {
        // Transient poll error — swallow and keep trying.
      }
      await new Promise((r) => setTimeout(r, everyMs));
    }
    return { ok: false, error: 'Timed out after 20 minutes' };
  };

  const regenerateDraft = async (slug) => {
    if (!window.confirm(`Regenerate /blog/${slug} from scratch? This will replace the current title, excerpt and body but keep the URL.`)) return;
    setRegenBusySlug(slug);
    const toastId = toast.loading(`Regenerating /blog/${slug}… this can take 30-90s`);
    try {
      const startRes = await axios.post(`${API}/admin/blog/regenerate`, { slug }, { headers: authHeaders() });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job_id in response');
      const out = await pollJob(jobId, {
        onTick: (j) => toast.loading(`Regenerating /blog/${slug}… ${Math.round(j.elapsed_seconds || 0)}s`, { id: toastId }),
      });
      if (out.ok) {
        toast.success(`Regenerated: ${(out.result?.new_title || '').slice(0, 60)}`, { id: toastId });
        fetchDrafts();
        fetchDashboardData();
      } else {
        toast.error(`Regenerate failed: ${out.error}`, { id: toastId });
      }
    } catch (e) {
      toast.error('Regenerate failed: ' + (e?.response?.data?.detail || e.message), { id: toastId });
    } finally {
      setRegenBusySlug(null);
    }
  };

  const [locScrubBusy, setLocScrubBusy] = useState(false);
  const [locScrubResult, setLocScrubResult] = useState(null);
  const doLocationScrub = async (dryRun = false) => {
    setLocScrubBusy(true);
    setLocScrubResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/blog/scrub-location`,
        { dry_run: dryRun },
        { headers: authHeaders() },
      );
      setLocScrubResult(res.data);
      toast.success(dryRun
        ? `Preview: ${res.data.updated} of ${res.data.scanned} posts would be scrubbed`
        : `Scrubbed location wording from ${res.data.updated} posts`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Location scrub failed';
      toast.error(typeof msg === 'string' ? msg : 'Location scrub failed');
    } finally {
      setLocScrubBusy(false);
    }
  };

  // Slug dedupe (fixes GSC "Discovered - currently not indexed" clusters)
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const [dedupeResult, setDedupeResult] = useState(null);

  // Bulk SEO rewrite — batch-fix the 20 worst-performing posts
  const [bulkSeoBusy, setBulkSeoBusy] = useState(false);
  const [bulkSeoResult, setBulkSeoResult] = useState(null);
  const doBulkSeoRewrite = async ({ dryRun = false, limit = 20 } = {}) => {
    if (!dryRun && !window.confirm(`Rewrite title + meta on the ${limit} worst-performing published posts using the LLM and auto-apply the top option? This uses ~£0.02 of LLM credits and takes 5-10 minutes.`)) {
      return;
    }
    setBulkSeoBusy(true);
    setBulkSeoResult(null);
    const toastId = toast.loading(dryRun ? 'Previewing worst posts…' : `Rewriting ${limit} posts… (5-10 min)`);
    try {
      const startRes = await axios.post(
        `${API}/admin/blog/bulk-seo-rewrite`,
        { limit, dry_run: dryRun },
        { headers: authHeaders() },
      );
      if (dryRun) {
        setBulkSeoResult(startRes.data);
        toast.success(`Preview: ${startRes.data.would_rewrite} posts would be rewritten`, { id: toastId });
        return;
      }
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job_id in response');
      const out = await pollJob(jobId, {
        maxWaitMs: 20 * 60 * 1000,
        onTick: (j) => toast.loading(`Rewriting… ${Math.round(j.elapsed_seconds || 0)}s`, { id: toastId }),
      });
      if (out.ok) {
        setBulkSeoResult(out.result);
        toast.success(`Rewrote ${out.result.rewritten} of ${out.result.scanned} posts (${out.result.failed} failed)`, { id: toastId });
      } else {
        toast.error(`Bulk rewrite failed: ${out.error}`, { id: toastId });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Bulk rewrite failed';
      toast.error(typeof msg === 'string' ? msg : 'Bulk rewrite failed', { id: toastId });
    } finally {
      setBulkSeoBusy(false);
    }
  };
  const doSlugDedupe = async (dryRun = false) => {
    setDedupeBusy(true);
    setDedupeResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/blog/dedupe-slugs`,
        { dry_run: dryRun },
        { headers: authHeaders() },
      );
      setDedupeResult(res.data);
      const label = dryRun ? 'would 301' : '301-redirected';
      toast.success(`${res.data.clusters_found} duplicate clusters, ${res.data.would_redirect} posts ${label} to canonical`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Slug dedupe failed';
      toast.error(typeof msg === 'string' ? msg : 'Slug dedupe failed');
    } finally {
      setDedupeBusy(false);
    }
  };

  // Title normalisation (strips trailing \n from LLM-generated titles)
  const [normBusy, setNormBusy] = useState(false);
  const [normResult, setNormResult] = useState(null);
  const doNormaliseTitles = async (dryRun = false) => {
    setNormBusy(true);
    setNormResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/blog/normalise-titles`,
        { dry_run: dryRun },
        { headers: authHeaders() },
      );
      setNormResult(res.data);
      toast.success(dryRun
        ? `Preview: ${res.data.updated} of ${res.data.scanned} posts would be cleaned`
        : `Cleaned trailing whitespace on ${res.data.updated} posts`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Title normalise failed';
      toast.error(typeof msg === 'string' ? msg : 'Title normalise failed');
    } finally {
      setNormBusy(false);
    }
  };

  const [faqInjectBusy, setFaqInjectBusy] = useState(false);
  const [faqInjectResult, setFaqInjectResult] = useState(null);

  const [aiPreviewOpen, setAiPreviewOpen] = useState(false);
  const [aiPreviewLoading, setAiPreviewLoading] = useState(false);
  const [aiPreviewData, setAiPreviewData] = useState(null);
  const [aiPreviewError, setAiPreviewError] = useState(null);

  // Emergent Google OAuth flow.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const [googleAuthProcessing, setGoogleAuthProcessing] = useState(false);
  const startGoogleSignIn = () => {
    const redirectUrl = window.location.origin + '/admin';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // On mount, if the URL hash carries a session_id fragment, exchange it
  // for an admin bearer token via /api/admin/auth/google-session and then
  // clean the fragment out of the URL so a refresh doesn't retry it.
  useEffect(() => {
    if (!window.location.hash?.includes('session_id=')) return;
    const match = window.location.hash.match(/session_id=([^&]+)/);
    if (!match) return;
    const sessionId = decodeURIComponent(match[1]);
    setGoogleAuthProcessing(true);
    (async () => {
      try {
        const res = await axios.post(`${API}/admin/auth/google-session`, { session_id: sessionId });
        localStorage.setItem('adminToken', res.data.token);
        // Wipe the fragment so a refresh doesn't try to exchange it again.
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
        setIsAuthenticated(true);
        fetchDashboardData();
        toast.success(`Signed in as ${res.data.email}`);
      } catch (e) {
        const status = e?.response?.status;
        const msg = e?.response?.data?.detail || e?.message || 'Google sign-in failed';
        if (status === 403) {
          toast.error('This admin panel is restricted.');
        } else {
          toast.error(typeof msg === 'string' ? msg : 'Google sign-in failed');
        }
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } finally {
        setGoogleAuthProcessing(false);
      }
    })();
    // Runs once on mount only. fetchDashboardData is defined below the
    // hook site so we deliberately keep the dep array empty.
  }, []);

  const openAiPreview = async (slug) => {
    setAiPreviewOpen(true);
    setAiPreviewData(null);
    setAiPreviewError(null);
    setAiPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blog/ai-preview/${encodeURIComponent(slug)}`, {
        headers: authHeaders(),
      });
      setAiPreviewData(res.data);
    } catch (e) {
      setAiPreviewError(e?.response?.data?.detail || e?.message || 'Preview failed');
    } finally {
      setAiPreviewLoading(false);
    }
  };
  const doFaqInject = async ({ dryRun = false, onlyMissing = true } = {}) => {
    setFaqInjectBusy(true);
    setFaqInjectResult(null);
    const toastId = toast.loading(dryRun ? 'Previewing FAQ sweep…' : 'Injecting FAQs across all posts… (5-8 min)');
    try {
      const startRes = await axios.post(
        `${API}/admin/blog/inject-faqs`,
        { dry_run: dryRun, only_missing: onlyMissing },
        { headers: authHeaders() },
      );
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job_id in response');
      const out = await pollJob(jobId, {
        maxWaitMs: 20 * 60 * 1000,
        onTick: (j) => toast.loading(
          dryRun ? `Previewing FAQ sweep… ${Math.round(j.elapsed_seconds || 0)}s`
                 : `Injecting FAQs… ${Math.round(j.elapsed_seconds || 0)}s`,
          { id: toastId },
        ),
      });
      if (out.ok && out.result?.ok !== false) {
        setFaqInjectResult(out.result);
        toast.success(dryRun
          ? `Preview: ${out.result.updated} of ${out.result.scanned} posts would get FAQs`
          : `Injected FAQs into ${out.result.updated} posts (${out.result.failed} failed)`,
          { id: toastId });
      } else {
        toast.error(out.error || 'FAQ injection failed', { id: toastId });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'FAQ injection failed';
      toast.error(typeof msg === 'string' ? msg : 'FAQ injection failed', { id: toastId });
    } finally {
      setFaqInjectBusy(false);
    }
  };

  const fetchInsights = useCallback(async (days) => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;
    setInsightsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/events/summary?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInsights(res.data);
    } catch (e) {
      console.error('Failed to load insights:', e);
      toast.error('Failed to load insights');
    } finally {
      setInsightsLoading(false);
    }
  }, []);

  // ---- SEO Tools handlers ----
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('adminToken')}` });

  const fetchSeoStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/seo/status`, { headers: authHeaders() });
      setSeoStatus(res.data);
    } catch (e) {
      console.error('Failed to load SEO status:', e);
    }
  }, []);

  const fetchGsc = useCallback(async (days) => {
    setGscLoading(true);
    try {
      const res = await axios.get(`${API}/admin/seo/gsc/summary?days=${days}`, { headers: authHeaders() });
      setGsc(res.data);
    } catch (e) {
      setGsc({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setGscLoading(false);
    }
  }, []);

  const fetchBing = useCallback(async () => {
    setBingLoading(true);
    try {
      const res = await axios.get(`${API}/admin/seo/bing/summary`, { headers: authHeaders() });
      setBing(res.data);
    } catch (e) {
      setBing({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setBingLoading(false);
    }
  }, []);

  const doRebuildImages = async () => {
    if (!window.confirm('Reassign a unique topic-tagged image + alt text to every published blog post?')) return;
    setSeoBusy(true); setRebuildResult(null);
    try {
      const res = await axios.post(`${API}/admin/blog/rebuild-images`, {}, { headers: authHeaders() });
      setRebuildResult(res.data);
      toast.success(`Rebuilt ${res.data.total} images, ${res.data.duplicates} duplicates.`);
    } catch (e) { toast.error('Image rebuild failed'); }
    finally { setSeoBusy(false); }
  };

  const doRedditGenerate = async () => {
    if (!redditForm.title || !redditForm.subreddit) {
      toast.error('Question title and subreddit are required');
      return;
    }
    setRedditBusy(true); setRedditResult(null);
    const toastId = toast.loading('Writing your Reddit-Q&A post… (30-90s)');
    try {
      const startRes = await axios.post(`${API}/admin/reddit/generate-blog`, redditForm, { headers: authHeaders() });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job_id in response');
      const out = await pollJob(jobId, {
        onTick: (j) => toast.loading(`Writing your Reddit-Q&A post… ${Math.round(j.elapsed_seconds || 0)}s`, { id: toastId }),
      });
      if (out.ok && out.result?.ok !== false) {
        setRedditResult(out.result);
        toast.success('Blog generated and published!', { id: toastId });
        setRedditForm({ title: '', subreddit: 'podcasting', url: '', selftext: '' });
      } else {
        const err = out.error || out.result?.error || 'Generation failed';
        setRedditResult({ ok: false, error: err });
        toast.error(err, { id: toastId });
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Generation failed';
      setRedditResult({ ok: false, error: typeof msg === 'string' ? msg : 'Generation failed' });
      toast.error(typeof msg === 'string' ? msg : 'Generation failed', { id: toastId });
    }
    finally { setRedditBusy(false); }
  };

  const doIndexnow = async () => {
    setSeoBusy(true); setIndexnowResult(null);
    try {
      const res = await axios.post(`${API}/admin/indexnow/push-all`, {}, { headers: authHeaders() });
      setIndexnowResult(res.data);
      toast.success(`Pushed ${res.data.pushed} URLs to IndexNow`);
    } catch (e) {
      toast.error('IndexNow push failed');
    } finally { setSeoBusy(false); }
  };

  const [resubmitBusy, setResubmitBusy] = useState(false);
  const [resubmitResult, setResubmitResult] = useState(null);
  const doResubmitSitemap = async () => {
    setResubmitBusy(true); setResubmitResult(null);
    try {
      const res = await axios.post(`${API}/admin/seo/resubmit-sitemap`, {}, { headers: authHeaders(), timeout: 120000 });
      setResubmitResult(res.data);
      const gsc = res.data?.google_search_console?.ok ? 'Google OK' : 'Google skipped';
      toast.success(`Sitemap resubmitted. ${res.data.indexnow.pushed} URLs to IndexNow. ${gsc}.`);
    } catch (e) {
      toast.error('Resubmit failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setResubmitBusy(false); }
  };

  const doInterlink = async () => {
    setSeoBusy(true); setInterlinkResult(null);
    try {
      const res = await axios.post(`${API}/admin/seo/interlink`, {}, { headers: authHeaders() });
      setInterlinkResult(res.data);
      toast.success(`Updated ${res.data.updated}/${res.data.total_posts} posts`);
    } catch (e) {
      toast.error('Interlink pass failed');
    } finally { setSeoBusy(false); }
  };

  const [headingFixBusy, setHeadingFixBusy] = useState(false);
  const [headingFixResult, setHeadingFixResult] = useState(null);
  const doHeadingFix = async (dryRun = false) => {
    setHeadingFixBusy(true);
    setHeadingFixResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/blog/normalize-headings`,
        { dry_run: dryRun },
        { headers: authHeaders() },
      );
      setHeadingFixResult(res.data);
      if (dryRun) {
        toast.success(`Preview: ${res.data.updated} of ${res.data.scanned} posts would be fixed`);
      } else {
        toast.success(`Fixed headings on ${res.data.updated} of ${res.data.scanned} posts`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Heading fix failed';
      toast.error(typeof msg === 'string' ? msg : 'Heading fix failed');
    } finally {
      setHeadingFixBusy(false);
    }
  };

  const [qualityBusy, setQualityBusy] = useState(false);
  const [qualityResult, setQualityResult] = useState(null);
  const doQualitySweep = async (dryRun = false, unpublishRisky = true) => {
    setQualityBusy(true);
    setQualityResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/blog/quality-sweep`,
        { dry_run: dryRun, unpublish_risky: unpublishRisky },
        { headers: authHeaders() },
      );
      setQualityResult(res.data);
      if (dryRun) {
        toast.success(`Preview: ${res.data.content_updated} fixes, ${res.data.unpublished} would be unpublished`);
      } else {
        toast.success(`Cleaned ${res.data.content_updated} posts, unpublished ${res.data.unpublished} for manual review`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Quality sweep failed';
      toast.error(typeof msg === 'string' ? msg : 'Quality sweep failed');
    } finally {
      setQualityBusy(false);
    }
  };

  const [imgBusy, setImgBusy] = useState(false);
  const [imgResult, setImgResult] = useState(null);
  const doDedupeImages = async (dryRun = false) => {
    setImgBusy(true);
    setImgResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/blog/dedupe-images`,
        { dry_run: dryRun },
        { headers: authHeaders() },
      );
      setImgResult(res.data);
      toast.success(dryRun
        ? `Preview: ${res.data.updated} of ${res.data.scanned} posts would get a fresh image`
        : `Assigned ${res.data.updated} unique images with alt text and credits`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Image dedupe failed';
      toast.error(typeof msg === 'string' ? msg : 'Image dedupe failed');
    } finally {
      setImgBusy(false);
    }
  };

  const [datesBusy, setDatesBusy] = useState(false);
  const [datesResult, setDatesResult] = useState(null);
  const doRebaseDates = async (dryRun = false) => {
    setDatesBusy(true);
    setDatesResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/blog/rebase-dates`,
        { dry_run: dryRun },
        { headers: authHeaders() },
      );
      setDatesResult(res.data);
      toast.success(dryRun
        ? `Preview: ${res.data.scanned} posts would be rebased (${res.data.oldest_date} to ${res.data.newest_date})`
        : `Rebased ${res.data.updated} posts (${res.data.oldest_date} to ${res.data.newest_date})`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Rebase failed';
      toast.error(typeof msg === 'string' ? msg : 'Rebase failed');
    } finally {
      setDatesBusy(false);
    }
  };

  const [promiseBusy, setPromiseBusy] = useState(false);
  const [promiseResult, setPromiseResult] = useState(null);
  const doPromiseCheck = async (dryRun = false) => {
    setPromiseBusy(true);
    setPromiseResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/blog/promise-check`,
        { dry_run: dryRun },
        { headers: authHeaders() },
      );
      setPromiseResult(res.data);
      toast.success(dryRun
        ? `Preview: ${res.data.failing} of ${res.data.scanned} posts fail their title promise`
        : `Unpublished ${res.data.unpublished} posts that overpromised in their title`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Promise check failed';
      toast.error(typeof msg === 'string' ? msg : 'Promise check failed');
    } finally {
      setPromiseBusy(false);
    }
  };

  const [hubInterlinkBusy, setHubInterlinkBusy] = useState(false);
  const [hubInterlinkResult, setHubInterlinkResult] = useState(null);
  const doInterlinkHubs = async (dryRun = false) => {
    setHubInterlinkBusy(true);
    setHubInterlinkResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/blog/interlink-hubs`,
        { dry_run: dryRun },
        { headers: authHeaders() },
      );
      setHubInterlinkResult(res.data);
      toast.success(dryRun
        ? `Preview: ${res.data.updated} of ${res.data.scanned} posts would get a hub callout`
        : `Injected hub callouts into ${res.data.updated} posts`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Interlink sweep failed';
      toast.error(typeof msg === 'string' ? msg : 'Interlink sweep failed');
    } finally {
      setHubInterlinkBusy(false);
    }
  };

  const doSeoRewrite = async () => {
    if (!seoSlug) { toast.error('Pick a post first'); return; }
    setSeoBusy(true); setSeoOptions([]);
    const toastId = toast.loading(`Rewriting title/meta for /blog/${seoSlug}… (15-30s)`);
    try {
      const startRes = await axios.post(`${API}/admin/seo/rewrite-post`, { slug: seoSlug }, { headers: authHeaders() });
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error('No job_id in response');
      const out = await pollJob(jobId, {
        onTick: (j) => toast.loading(`Rewriting title/meta… ${Math.round(j.elapsed_seconds || 0)}s`, { id: toastId }),
      });
      if (out.ok) {
        const opts = out.result?.options || [];
        setSeoOptions(opts);
        if (opts.length === 0) {
          toast.error('LLM returned no options, try again', { id: toastId });
        } else {
          toast.success(`Got ${opts.length} title/meta options`, { id: toastId });
        }
      } else {
        toast.error(`Rewrite failed: ${out.error}`, { id: toastId });
      }
    } catch (e) {
      toast.error('LLM rewrite failed: ' + (e?.response?.data?.detail || e.message), { id: toastId });
    } finally { setSeoBusy(false); }
  };

  const applySeoRewrite = async (opt) => {
    if (!window.confirm(`Apply this rewrite to /blog/${seoSlug}?`)) return;
    setSeoBusy(true);
    try {
      await axios.post(`${API}/admin/seo/apply-rewrite`, {
        slug: seoSlug, title: opt.title, meta: opt.meta,
      }, { headers: authHeaders() });
      toast.success('Rewrite applied and pushed to IndexNow');
      setSeoOptions([]);
      fetchDashboardData();
    } catch (e) {
      toast.error('Failed to apply rewrite');
    } finally { setSeoBusy(false); }
  };

  // --- Wrong-topic query actions (added Feb 2026) -----------------------
  // When Jo clicks "Fix this" on a wrong-topic row we hit GSC for the
  // actual pages ranking on that query, then let her either rewrite the
  // title/meta or unpublish the page in one click.
  const toggleWrongTopicPages = async (q) => {
    if (wtExpanded[q]?.pages || wtExpanded[q]?.loading) {
      // Collapse if already fetched or in-flight
      setWtExpanded((s) => ({ ...s, [q]: { ...s[q], open: !s[q].open } }));
      return;
    }
    setWtExpanded((s) => ({ ...s, [q]: { loading: true, open: true } }));
    try {
      const res = await axios.get(`${API}/admin/seo/gsc/pages-for-query`, {
        headers: authHeaders(),
        params: { q, days: 90 },
      });
      if (res.data?.ok) {
        setWtExpanded((s) => ({ ...s, [q]: { loading: false, open: true, pages: res.data.pages || [] } }));
      } else {
        setWtExpanded((s) => ({ ...s, [q]: { loading: false, open: true, error: res.data?.error || 'GSC lookup failed' } }));
      }
    } catch (e) {
      setWtExpanded((s) => ({ ...s, [q]: { loading: false, open: true, error: 'Network error' } }));
    }
  };

  const rewritePageBySlug = (slug) => {
    setSeoSlug(slug);
    setSeoOptions([]);
    // Scroll the rewriter card into view so the action feels connected.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="seo-rewriter-card"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
    toast.success('Ready — click "Get 3 options" to rewrite');
  };

  const unpublishBySlug = async (slug) => {
    if (!window.confirm(`Unpublish /blog/${slug}? It will disappear from the site and Google will drop it on next crawl.`)) return;
    try {
      await axios.put(`${API}/blog/${slug}`, { published: false }, { headers: authHeaders() });
      toast.success(`/blog/${slug} unpublished`);
      fetchDashboardData();
    } catch (e) {
      toast.error('Failed to unpublish');
    }
  };

  const pageToSlug = (url) => {
    // https://audioandco.com/blog/foo -> foo. Returns "" if not a blog URL.
    if (!url) return '';
    const m = url.match(/\/blog\/([a-z0-9-]+)\/?$/i);
    return m ? m[1] : '';
  };


  const fetchDashboardData = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [leadsRes, contactsRes, downloadConsentsRes, blogRes, servicesRes, packagesRes, podcastsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/contact`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/download-consents`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/blog?published_only=false`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/services`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/packages`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/podcasts`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setLeads(leadsRes.data);
      setContacts(contactsRes.data);
      setDownloadConsents(downloadConsentsRes.data);
      setBlogPosts(blogRes.data);
      setServices(servicesRes.data);
      setPackages(packagesRes.data);
      setPodcasts(podcastsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;
    // Validate the stored token against the backend before trusting it.
    // Any authenticated admin endpoint works; we use /admin/blog/drafts
    // because it's small, fast and definitely admin-gated. A stale token
    // would otherwise leave the UI looking logged in whilst every API
    // call 401s (the exact "invalid or expired token on everything"
    // production symptom we're recovering from).
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/admin/blog/drafts`, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        setIsAuthenticated(true);
        fetchDashboardData();
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          localStorage.removeItem('adminToken');
          setIsAuthenticated(false);
          toast.error('Session expired. Please sign in again.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [fetchDashboardData]);

  // Global 401 interceptor: any authenticated admin call that returns 401
  // means the token has gone stale mid-session (e.g. server restart wiped
  // it, or it just expired). Clear localStorage and drop the user back to
  // the login screen with a clear message rather than leaving them stuck
  // on a broken dashboard.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const url = error?.config?.url || '';
        const status = error?.response?.status;
        const isAdminCall = url.includes('/admin/') || url.includes('/email-previews');
        const isLoginCall = url.endsWith('/admin/login');
        if (status === 401 && isAdminCall && !isLoginCall) {
          if (localStorage.getItem('adminToken')) {
            localStorage.removeItem('adminToken');
            setIsAuthenticated(false);
            toast.error('Session expired. Please sign in again.');
          }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard' && isAuthenticated) {
      fetchDrafts();
    }
    if (activeTab === 'drip' && isAuthenticated) {
      fetchDripStats();
    }
    if (activeTab === 'emails' && isAuthenticated && !emailPreviews) {
      fetchEmailPreviews();
    }
    if (activeTab === 'insights' && isAuthenticated) {
      fetchInsights(insightsDays);
    }
    if (activeTab === 'seo' && isAuthenticated && !seoStatus) {
      fetchSeoStatus();
    }
    if (activeTab === 'seo' && isAuthenticated && !gsc) {
      fetchGsc(gscDays);
    }
    if (activeTab === 'seo' && isAuthenticated && !bing) {
      fetchBing();
    }
  }, [activeTab, isAuthenticated, fetchDripStats, emailPreviews, fetchEmailPreviews, fetchInsights, insightsDays, fetchSeoStatus, seoStatus, fetchGsc, gsc, gscDays, fetchBing, bing, fetchDrafts]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      localStorage.setItem('adminToken', response.data.token);
      setIsAuthenticated(true);
      toast.success('Logged in successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    // Best-effort server-side revoke. Never blocks the UI.
    if (token) {
      try {
        await axios.post(`${API}/admin/auth/logout`, { token });
      } catch (_) { /* revoke may fail if backend is down; that's fine */ }
    }
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = (data, type) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }

    let headers = [];
    let rows = [];

    if (type === 'leads') {
      headers = ['Name', 'Email', 'Phone', 'Company', 'Services Interested', 'GDPR Consent', 'Marketing Consent', 'Source', 'Status', 'Date'];
      rows = data.map(lead => [
        lead.name || '',
        lead.email || '',
        lead.phone || '',
        lead.company || '',
        (lead.services_interested || []).join('; '),
        lead.gdpr_consent ? 'Yes' : 'No',
        lead.marketing_consent ? 'Yes' : 'No',
        lead.source || '',
        lead.status || 'new',
        formatDate(lead.created_at)
      ]);
    } else if (type === 'download_consents') {
      headers = ['First Name', 'Surname', 'Email', 'Terms Agreed', 'Marketing Consent', 'Download Type', 'Status', 'Date'];
      rows = data.map(consent => [
        consent.first_name || '',
        consent.surname || '',
        consent.email || '',
        consent.agreed_to_terms ? 'Yes' : 'No',
        consent.agreed_to_marketing ? 'Yes' : 'No',
        consent.download_type || '',
        consent.status || 'new',
        formatDate(consent.created_at)
      ]);
    } else if (type === 'contacts') {
      headers = ['Name', 'Email', 'Phone', 'Company', 'Service Category', 'Budget', 'Message', 'Date'];
      rows = data.map(contact => [
        contact.name || '',
        contact.email || '',
        contact.phone || '',
        contact.company || '',
        contact.service_category || '',
        contact.project_budget || '',
        (contact.message || '').replace(/\n/g, ' ').replace(/,/g, ';'),
        formatDate(contact.created_at)
      ]);
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${type}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('CSV exported successfully!');
  };

  const updateStatus = async (id, newStatus, type) => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const endpoint = type === 'lead' ? `/leads/${id}/status` : `/download-consents/${id}/status`;
      await axios.patch(`${API}${endpoint}`, { status: newStatus }, { headers });
      toast.success('Status updated successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const viewDetails = (item, type) => {
    setSelectedItem({ ...item, type });
    setShowDetailsModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // CRUD Functions
  const deleteBlogPost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      await axios.delete(`${API}/blog/${postId}`, { headers });
      toast.success('Blog post deleted successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast.error('Failed to delete blog post');
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      await axios.delete(`${API}/services/${serviceId}`, { headers });
      toast.success('Service deleted successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const deletePodcast = async (podcastId) => {
    if (!window.confirm('Are you sure you want to delete this podcast?')) return;
    
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      await axios.delete(`${API}/podcasts/${podcastId}`, { headers });
      toast.success('Podcast deleted successfully');
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting podcast:', error);
      toast.error('Failed to delete podcast');
    }
  };

  const saveBlogPost = async (postData) => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    setFormLoading(true);
    
    try {
      if (editingItem) {
        // Legacy posts (generated by Reddit/content-sprint) may lack an `id`,
        // so fall back to slug. Backend accepts either.
        const key = editingItem.id || editingItem.slug;
        await axios.put(`${API}/blog/${key}`, postData, { headers });
        toast.success('Blog post updated successfully');
      } else {
        await axios.post(`${API}/blog`, postData, { headers });
        toast.success('Blog post created successfully');
      }
      setShowBlogForm(false);
      setEditingItem(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast.error('Failed to save blog post');
    } finally {
      setFormLoading(false);
    }
  };

  const saveService = async (serviceData) => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    setFormLoading(true);
    
    try {
      if (editingItem) {
        await axios.put(`${API}/services/${editingItem.id}`, serviceData, { headers });
        toast.success('Service updated successfully');
      } else {
        await axios.post(`${API}/services`, serviceData, { headers });
        toast.success('Service created successfully');
      }
      setShowServiceForm(false);
      setEditingItem(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    } finally {
      setFormLoading(false);
    }
  };

  const savePodcast = async (podcastData) => {
    const token = localStorage.getItem('adminToken');
    const headers = { Authorization: `Bearer ${token}` };
    setFormLoading(true);
    
    try {
      if (editingItem) {
        await axios.put(`${API}/podcasts/${editingItem.id}`, podcastData, { headers });
        toast.success('Podcast updated successfully');
      } else {
        await axios.post(`${API}/podcasts`, podcastData, { headers });
        toast.success('Podcast added successfully');
      }
      setShowPodcastForm(false);
      setEditingItem(null);
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving podcast:', error);
      toast.error('Failed to save podcast');
    } finally {
      setFormLoading(false);
    }
  };

  // Details Modal Component
  const DetailsModal = () => {
    if (!selectedItem) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowDetailsModal(false)}>
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Submission Details</h3>
                <p className="text-sm text-gray-500">
                  Type: <span className="font-medium">{selectedItem.type === 'lead' ? 'Lead Capture' : selectedItem.type === 'download_consent' ? 'Newsletter Signup' : 'Contact Form'}</span>
                </p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="space-y-4">
              {/* Personal Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Users className="w-4 h-4 mr-2" />
                  Personal Information
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedItem.first_name && (
                    <div>
                      <p className="text-gray-500">First Name</p>
                      <p className="font-medium text-gray-900">{selectedItem.first_name}</p>
                    </div>
                  )}
                  {selectedItem.surname && (
                    <div>
                      <p className="text-gray-500">Surname</p>
                      <p className="font-medium text-gray-900">{selectedItem.surname}</p>
                    </div>
                  )}
                  {selectedItem.name && (
                    <div>
                      <p className="text-gray-500">Full Name</p>
                      <p className="font-medium text-gray-900">{selectedItem.name}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{selectedItem.email}</p>
                  </div>
                  {selectedItem.phone && (
                    <div>
                      <p className="text-gray-500">Phone</p>
                      <p className="font-medium text-gray-900">{selectedItem.phone}</p>
                    </div>
                  )}
                  {selectedItem.company && (
                    <div>
                      <p className="text-gray-500">Company</p>
                      <p className="font-medium text-gray-900">{selectedItem.company}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Services/Interests */}
              {selectedItem.services_interested && selectedItem.services_interested.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Services Interested</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedItem.services_interested.map((service, idx) => (
                      <span key={idx} className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedItem.service_category && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Service Category</h4>
                  <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full">
                    {selectedItem.service_category}
                  </span>
                  {selectedItem.project_budget && (
                    <div className="mt-3">
                      <p className="text-gray-500 text-sm">Project Budget</p>
                      <p className="font-medium text-gray-900">{selectedItem.project_budget}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Message */}
              {selectedItem.message && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">Message</h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedItem.message}</p>
                </div>
              )}

              {/* Consent Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Consent & Permissions
                </h4>
                <div className="space-y-2 text-sm">
                  {(selectedItem.gdpr_consent !== undefined || selectedItem.agreed_to_terms !== undefined) && (
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded flex items-center justify-center mr-3 ${
                        (selectedItem.gdpr_consent || selectedItem.agreed_to_terms) ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {(selectedItem.gdpr_consent || selectedItem.agreed_to_terms) ? (
                          <CheckCircle className="w-3 h-3 text-green-600" />
                        ) : (
                          <X className="w-3 h-3 text-red-600" />
                        )}
                      </div>
                      <span className="text-gray-700">
                        {selectedItem.gdpr_consent !== undefined ? 'GDPR Consent' : 'Terms & Conditions'}: 
                        <span className="font-medium ml-1">
                          {(selectedItem.gdpr_consent || selectedItem.agreed_to_terms) ? 'Agreed' : 'Not Agreed'}
                        </span>
                      </span>
                    </div>
                  )}
                  {(selectedItem.marketing_consent !== undefined || selectedItem.agreed_to_marketing !== undefined) && (
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded flex items-center justify-center mr-3 ${
                        (selectedItem.marketing_consent || selectedItem.agreed_to_marketing) ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        {(selectedItem.marketing_consent || selectedItem.agreed_to_marketing) ? (
                          <CheckCircle className="w-3 h-3 text-green-600" />
                        ) : (
                          <X className="w-3 h-3 text-gray-400" />
                        )}
                      </div>
                      <span className="text-gray-700">
                        Marketing Consent: 
                        <span className="font-medium ml-1">
                          {(selectedItem.marketing_consent || selectedItem.agreed_to_marketing) ? 'Yes' : 'No'}
                        </span>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-3">Additional Information</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Source</p>
                    <p className="font-medium text-gray-900">{selectedItem.source || selectedItem.download_type || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedItem.status || 'new')}`}>
                      {(selectedItem.status || 'new').toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-gray-500">Submission Date</p>
                    <p className="font-medium text-gray-900">{formatDate(selectedItem.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">ID</p>
                    <p className="font-medium text-gray-900 text-xs">{selectedItem.id}</p>
                  </div>
                </div>
              </div>

              {/* Follow-up Action - only show urgently if they requested a call */}
              {(selectedItem.type === 'lead' || selectedItem.type === 'download_consent') && (
                selectedItem.discovery_call ? (
                  <div className="bg-red-50 border-2 border-red-600 p-4 rounded-lg">
                    <h4 className="font-semibold text-red-600 mb-2 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      ACTION REQUIRED: Discovery Call Requested
                    </h4>
                    <p className="text-sm text-gray-800 mb-3">
                      This person ticked <strong>"Book me in for a FREE discovery call"</strong> when submitting their audit.
                      {selectedItem.status === 'new' && ' They are waiting for your response.'}
                    </p>
                    <div className="bg-white p-3 rounded border border-red-200">
                      <p className="text-xs text-gray-600 mb-2">Send them this booking link:</p>
                      <div className="flex items-center space-x-2">
                        <input 
                          type="text" 
                          readOnly 
                          value="https://calendar.app.google/WpTCsqBfRuKw85KP7"
                          className="flex-1 px-3 py-2 bg-gray-50 text-sm border border-gray-300 rounded"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('https://calendar.app.google/WpTCsqBfRuKw85KP7');
                            toast.success('Booking link copied!');
                          }}
                          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h4 className="font-semibold text-blue-700 mb-2 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Suggested Follow-Up
                    </h4>
                    <p className="text-sm text-gray-800 mb-3">
                      They didn't request a call, but you could still offer one.
                    </p>
                    <div className="bg-white p-3 rounded border border-blue-200">
                      <p className="text-xs text-gray-600 mb-2">Booking link:</p>
                      <div className="flex items-center space-x-2">
                        <input 
                          type="text" 
                          readOnly 
                          value="https://calendar.app.google/WpTCsqBfRuKw85KP7"
                          className="flex-1 px-3 py-2 bg-gray-50 text-sm border border-gray-300 rounded"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText('https://calendar.app.google/WpTCsqBfRuKw85KP7');
                            toast.success('Booking link copied!');
                          }}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Admin Portal
            </h2>
            <p className="text-gray-600">
              Sign in with the Audio &amp; Co. Google account.
            </p>
          </div>

          {googleAuthProcessing ? (
            <div className="text-center py-6 text-gray-600" data-testid="admin-google-processing">
              Signing you in with Google...
            </div>
          ) : (
            <button
              type="button"
              onClick={startGoogleSignIn}
              className="w-full inline-flex items-center justify-center gap-3 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-900 font-semibold py-3 px-4 rounded-md shadow-sm transition"
              data-testid="admin-google-signin"
            >
              {/* Google G logo, inline SVG so no external asset needed */}
              <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Sign in with Google
            </button>
          )}

          <p className="text-center text-xs text-gray-500 pt-2">
            Access is restricted to allow-listed Google accounts. Any other account will be refused.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'insights', label: 'Insights', icon: TrendingUp },
    { id: 'rankings', label: 'Rankings', icon: TrendingUp },
    { id: 'seo', label: 'SEO Tools', icon: Globe },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'estimator', label: 'Quote Leads', icon: Package },
    { id: 'audits', label: 'Audits', icon: FileText },
    { id: 'contacts', label: 'Contacts', icon: Mail },
    { id: 'drip', label: 'Welcome Sequence', icon: Send },
    { id: 'emails', label: 'Email Previews', icon: Mail },
    { id: 'blog', label: 'Blog Posts', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* AI Overview preview modal */}
      {aiPreviewOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setAiPreviewOpen(false)}
          data-testid="ai-preview-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5 text-purple-600" /> AI Overview Preview
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">How Google, Perplexity, ChatGPT and Bing Copilot see this post.</p>
              </div>
              <button
                onClick={() => setAiPreviewOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-700 rounded"
                data-testid="ai-preview-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {aiPreviewLoading && <p className="text-sm text-gray-600">Building preview...</p>}
              {aiPreviewError && (
                <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{aiPreviewError}</p>
              )}
              {aiPreviewData && (
                <>
                  {/* Google-style snippet card */}
                  <section>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">Search result snippet</p>
                    <div className="mt-2 p-4 border border-gray-300 rounded-lg bg-white shadow-sm">
                      <p className="text-xs text-gray-600 truncate">{aiPreviewData.canonical_url}</p>
                      <h4 className="text-blue-700 text-lg font-medium mt-1 hover:underline cursor-default">{aiPreviewData.snippet.title}</h4>
                      <p className="text-sm text-gray-700 mt-1 leading-snug">{aiPreviewData.snippet.meta_description}</p>
                    </div>
                  </section>

                  {/* AI overview lead */}
                  <section>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">AI Overview lead (first quoted line)</p>
                    <div className="mt-2 p-4 bg-purple-50 border border-purple-200 rounded-lg text-sm text-gray-800 italic leading-relaxed">
                      &ldquo;{aiPreviewData.ai_lead || 'No lead paragraph detected.'}&rdquo;
                    </div>
                  </section>

                  {/* Schema types */}
                  <section>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">Schema.org types emitted</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {aiPreviewData.schema_types.map((t) => (
                        <span key={t} className="text-xs font-mono bg-gray-900 text-white px-2 py-1 rounded">{t}</span>
                      ))}
                    </div>
                  </section>

                  {/* Section list (H2s) */}
                  <section>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">Extractable sections ({aiPreviewData.section_list.length})</p>
                    <ul className="mt-2 text-sm text-gray-700 space-y-1 max-h-40 overflow-auto bg-gray-50 border border-gray-200 rounded p-3">
                      {aiPreviewData.section_list.length === 0 && <li className="text-gray-400 italic">No H2 sections found. Add ## headings.</li>}
                      {aiPreviewData.section_list.map((h, i) => (
                        <li key={i}>&bull; {h}</li>
                      ))}
                    </ul>
                  </section>

                  {/* FAQs */}
                  <section>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">
                      FAQ pool (source: <span className="font-mono text-gray-700">{aiPreviewData.faqs.used_source}</span>)
                    </p>
                    {aiPreviewData.faqs.stored.length > 0 ? (
                      <ol className="mt-2 space-y-2">
                        {aiPreviewData.faqs.stored.map((f, i) => (
                          <li key={i} className="p-3 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-sm font-semibold text-gray-900">{f.q}</p>
                            <p className="text-xs text-gray-700 mt-1 leading-relaxed">{f.a}</p>
                          </li>
                        ))}
                      </ol>
                    ) : aiPreviewData.faqs.body_extracted.length > 0 ? (
                      <ol className="mt-2 space-y-2">
                        {aiPreviewData.faqs.body_extracted.map((f, i) => (
                          <li key={i} className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                            <p className="text-sm font-semibold text-gray-900">{f.q}</p>
                            <p className="text-xs text-gray-700 mt-1 leading-relaxed">{f.a}</p>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <p className="mt-2 text-sm text-gray-500 italic">No FAQs yet. Run "Auto-inject FAQs" in the SEO tab to add 4.</p>
                    )}
                  </section>

                  {/* HowTo */}
                  {aiPreviewData.howto && (
                    <section>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">
                        HowTo rich result ({aiPreviewData.howto.step_count} of {aiPreviewData.howto.promised_count} promised steps)
                      </p>
                      <ol className="mt-2 space-y-1 text-sm text-gray-700 list-decimal list-inside bg-emerald-50 border border-emerald-200 rounded p-3">
                        {aiPreviewData.howto.steps.map((s, i) => (
                          <li key={i}><span className="font-semibold">{s.name}</span></li>
                        ))}
                      </ol>
                    </section>
                  )}

                  {/* Stats */}
                  <p className="text-xs text-gray-500 border-t border-gray-200 pt-3">
                    {aiPreviewData.stats.content_chars.toLocaleString()} chars &middot; {aiPreviewData.stats.h2_count} H2s
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setMobileNavOpen(v => !v)}
                className="lg:hidden p-2 -ml-2 rounded-md hover:bg-gray-100 text-gray-700"
                aria-label="Toggle navigation"
                data-testid="admin-mobile-nav-toggle"
              >
                {mobileNavOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="text-base sm:text-2xl font-bold text-gray-900 truncate">
                <span className="hidden sm:inline">Audio &amp; Co. Admin Portal</span>
                <span className="sm:hidden">Admin</span>
              </h1>
            </div>
            <button
              onClick={handleLogout}
              className="bg-gray-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-gray-700 transition-colors text-sm sm:text-base flex-shrink-0"
              data-testid="admin-logout-btn"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="flex flex-col lg:flex-row lg:space-x-8">
          {/* Sidebar (desktop) + Drawer (mobile) */}
          <div className={`${mobileNavOpen ? 'block' : 'hidden'} lg:block lg:w-64 lg:flex-shrink-0 mb-4 lg:mb-0`}>
            <nav className="space-y-1.5 lg:space-y-2 bg-white lg:bg-transparent border lg:border-0 border-gray-200 rounded-lg p-2 lg:p-0">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setMobileNavOpen(false); }}
                    className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors min-h-[44px] ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700 font-semibold'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`admin-tab-${tab.id}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="truncate">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {activeTab === 'dashboard' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
                </div>
                
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <Users className="w-8 h-8 text-blue-600" />
                      <div className="ml-4">
                        <p className="text-sm text-gray-600">Total Leads</p>
                        <p className="text-2xl font-bold text-gray-900">{leads.length + downloadConsents.length}</p>
                        <p className="text-xs text-gray-500">{leads.length} modal + {downloadConsents.length} newsletter</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <Mail className="w-8 h-8 text-green-600" />
                      <div className="ml-4">
                        <p className="text-sm text-gray-600">Contact Inquiries</p>
                        <p className="text-2xl font-bold text-gray-900">{contacts.length}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center">
                      <FileText className="w-8 h-8 text-green-600" />
                      <div className="ml-4">
                        <p className="text-sm text-gray-600">Blog Posts</p>
                        <p className="text-2xl font-bold text-gray-900">{blogPosts.length}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="bg-white rounded-lg shadow" data-testid="dashboard-recent-leads">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Leads</h3>
                  </div>
                  <div className="p-6">
                    {leads.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No leads yet</p>
                    ) : (
                      <div className="space-y-4">
                        {leads.slice(0, 5).map((lead) => (
                          <div key={lead.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                            <div>
                              <p className="font-semibold text-gray-900">{lead.name}</p>
                              <p className="text-sm text-gray-600">{lead.email}</p>
                              <p className="text-xs text-gray-500">
                                Interested in: {lead.services_interested?.join(', ') || 'General inquiry'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-500">{formatDate(lead.created_at)}</p>
                              {lead.marketing_consent && (
                                <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                  Marketing OK
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Unpublished drafts */}
                <div className="bg-white rounded-lg shadow" data-testid="dashboard-drafts">
                  <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Unpublished blog drafts</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Auto-generated posts wait here for your approval, along with any post held back by the quality checks. Nothing here is visible to Google until you press Publish.</p>
                    </div>
                    <span className="text-2xl font-bold text-amber-600">{drafts.count}</span>
                  </div>
                  <div className="p-6">
                    {drafts.count === 0 ? (
                      <p className="text-gray-500 text-center py-4">No drafts. Every published post is live.</p>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {drafts.posts.map((p) => (
                          <li key={p.slug} className="py-3 flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-900 hover:text-[#A7468A] truncate block">
                                {p.title || p.slug}
                              </a>
                              <p className="text-xs text-gray-500 font-mono truncate">/blog/{p.slug}</p>
                              {p.unpublish_reason && (
                                <p className="text-xs text-amber-700 mt-0.5">{p.unpublish_reason}</p>
                              )}
                            </div>
                            <div className="flex-shrink-0 flex gap-2">
                              <button
                                onClick={() => regenerateDraft(p.slug)}
                                disabled={regenBusySlug === p.slug}
                                className="bg-indigo-600 text-white text-xs font-semibold px-3 py-1.5 rounded hover:bg-indigo-700 disabled:opacity-50"
                                data-testid={`regenerate-${p.slug}`}
                                title="Regenerate this post from scratch using the current clean voice guide"
                              >
                                {regenBusySlug === p.slug ? 'Regenerating...' : 'Regenerate'}
                              </button>
                              <button
                                onClick={() => republishDraft(p.slug)}
                                disabled={regenBusySlug === p.slug}
                                className="bg-green-600 text-white text-xs font-semibold px-3 py-1.5 rounded hover:bg-green-700 disabled:opacity-50"
                                data-testid={`republish-${p.slug}`}
                              >
                                Publish
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}


            {activeTab === 'insights' && (
              <div className="space-y-6" data-testid="admin-insights">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Conversion Insights</h2>
                    <p className="text-sm text-gray-600 mt-1">Sticky mobile bar rotations, Bili chip clicks, header CTA clicks.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={insightsDays}
                      onChange={(e) => setInsightsDays(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="insights-range"
                    >
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                      <option value={365}>Last 12 months</option>
                    </select>
                    <button
                      onClick={() => fetchInsights(insightsDays)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                      disabled={insightsLoading}
                      data-testid="insights-refresh"
                    >
                      <RefreshCw className={`w-4 h-4 ${insightsLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </div>

                {insightsLoading && !insights && (
                  <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">Loading conversion data…</div>
                )}

                {insights && (
                  <>
                    {/* Top-line totals */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: 'Sticky impressions', val: insights.totals.sticky_impressions },
                        { label: 'Sticky clicks',      val: insights.totals.sticky_clicks },
                        { label: 'Bili opens',         val: insights.totals.bili_opens },
                        { label: 'Bili chip clicks',   val: insights.totals.bili_chip_clicks },
                      ].map((t) => (
                        <div key={t.label} className="bg-white rounded-lg shadow p-4">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{t.label}</p>
                          <p className="text-3xl font-bold text-gray-900 mt-1">{t.val.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>

                    {/* Sticky bar CTR by pitch */}
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="px-4 sm:px-6 py-3 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-900">Mobile sticky bar, click-through by pitch</h3>
                        <p className="text-xs text-gray-500 mt-1">The rotating headline that got the most clicks per impression is your top-performing pitch.</p>
                      </div>
                      {insights.sticky_ctr.length === 0 ? (
                        <div className="p-6 text-center text-gray-500 text-sm">No mobile sticky data yet in this window.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                              <tr>
                                <th className="text-left px-4 sm:px-6 py-2">Pitch</th>
                                <th className="text-right px-4 py-2">Impressions</th>
                                <th className="text-right px-4 py-2">Clicks</th>
                                <th className="text-right px-4 sm:px-6 py-2">CTR</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {insights.sticky_ctr.map((r) => (
                                <tr key={r.label} data-testid={`insights-pitch-${(r.label||'').replace(/\s+/g,'-').toLowerCase()}`}>
                                  <td className="px-4 sm:px-6 py-3 font-medium text-gray-900">{r.label || '(unlabeled)'}</td>
                                  <td className="px-4 py-3 text-right text-gray-700">{r.impressions.toLocaleString()}</td>
                                  <td className="px-4 py-3 text-right text-gray-700">{r.clicks.toLocaleString()}</td>
                                  <td className="px-4 sm:px-6 py-3 text-right font-semibold text-red-600">{r.ctr_pct}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Bili chip clicks */}
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="px-4 sm:px-6 py-3 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-900">Bili quick-reply chips, clicks by label</h3>
                        <p className="text-xs text-gray-500 mt-1">Which pre-canned service the visitor tapped when they opened Bili.</p>
                      </div>
                      {(() => {
                        const chipRows = insights.by_event_label.filter(r => r.event === 'bili_chip_click');
                        if (chipRows.length === 0) return <div className="p-6 text-center text-gray-500 text-sm">No chip clicks yet in this window.</div>;
                        return (
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                                <tr>
                                  <th className="text-left px-4 sm:px-6 py-2">Chip</th>
                                  <th className="text-right px-4 sm:px-6 py-2">Clicks</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {chipRows.map((r) => (
                                  <tr key={r.label} data-testid={`insights-chip-${(r.label||'').replace(/\s+/g,'-').toLowerCase()}`}>
                                    <td className="px-4 sm:px-6 py-3 font-medium text-gray-900">{r.label}</td>
                                    <td className="px-4 sm:px-6 py-3 text-right font-semibold text-gray-900">{r.count.toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Full event breakdown */}
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                      <div className="px-4 sm:px-6 py-3 border-b border-gray-200">
                        <h3 className="font-semibold text-gray-900">All events</h3>
                        <p className="text-xs text-gray-500 mt-1">Raw event x label counts over the selected window.</p>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                            <tr>
                              <th className="text-left px-4 sm:px-6 py-2">Event</th>
                              <th className="text-left px-4 py-2">Label</th>
                              <th className="text-right px-4 py-2">Count</th>
                              <th className="text-left px-4 sm:px-6 py-2">Last seen</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {insights.by_event_label.map((r, i) => (
                              <tr key={`${r.event}-${r.label}-${i}`}>
                                <td className="px-4 sm:px-6 py-3 font-mono text-xs text-gray-700">{r.event}</td>
                                <td className="px-4 py-3 text-gray-900">{r.label || '(none)'}</td>
                                <td className="px-4 py-3 text-right font-semibold text-gray-900">{r.count.toLocaleString()}</td>
                                <td className="px-4 sm:px-6 py-3 text-gray-500 text-xs">{r.last ? formatDate(r.last) : ''}</td>
                              </tr>
                            ))}
                            {insights.by_event_label.length === 0 && (
                              <tr><td className="px-6 py-6 text-center text-gray-500" colSpan={4}>No events tracked yet in this window.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      Data since {new Date(insights.since).toLocaleString('en-GB')} · {insights.total_events.toLocaleString()} events total
                    </p>
                  </>
                )}
              </div>
            )}


            {activeTab === 'seo' && (
              <div className="space-y-6" data-testid="admin-seo-tools">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">SEO Tools</h2>
                  <p className="text-sm text-gray-600 mt-1">Compounding-authority controls: instant Bing/Yandex indexing, LLM title/meta rewriter, internal-linking pass, and integration health.</p>
                </div>

                {/* Status */}
                {seoStatus && (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                      { k: 'Published blog posts',       v: seoStatus.blog_count, ok: true },
                      { k: 'Sitemap',                    v: 'Live', href: seoStatus.sitemap_url, ok: true },
                      { k: 'llms.txt (AI crawlers)',     v: 'Live', href: seoStatus.llms_txt_url, ok: true },
                      { k: 'ai.txt (AI crawlers)',       v: 'Live', href: seoStatus.ai_txt_url, ok: true },
                      { k: 'IndexNow key',               v: 'Live', href: seoStatus.indexnow_key_url, ok: true },
                      { k: 'Robots.txt',                 v: 'Live', href: seoStatus.robots_url, ok: true },
                      { k: 'Google Search Console',      v: seoStatus.gsc_configured ? 'Connected' : 'Not connected', ok: seoStatus.gsc_configured },
                      { k: 'Bing Webmaster',             v: seoStatus.bing_webmaster_configured ? 'Connected' : 'Not connected', ok: seoStatus.bing_webmaster_configured },
                    ].map((s) => (
                      <div key={s.k} className="bg-white rounded-lg shadow p-4">
                        <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">{s.k}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {s.href ? (
                            <a href={s.href} target="_blank" rel="noopener noreferrer" className={`text-lg font-bold ${s.ok ? 'text-green-700' : 'text-red-600'} underline`}>{s.v}</a>
                          ) : (
                            <span className={`text-lg font-bold ${s.ok ? 'text-gray-900' : 'text-red-600'}`}>{s.v}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* IndexNow push */}
                <div className="bg-white rounded-lg shadow p-5" data-testid="seo-indexnow-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="font-semibold text-gray-900">Push all URLs to Bing / Yandex / Naver</h3>
                      <p className="text-sm text-gray-600 mt-1">Instantly notifies IndexNow-supporting engines to recrawl every canonical page + all blog posts. Use after big edits.</p>
                    </div>
                    <button
                      onClick={doIndexnow}
                      disabled={seoBusy}
                      className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      data-testid="seo-indexnow-run"
                    >
                      <Zap className="w-4 h-4" /> Push now
                    </button>
                  </div>
                  {indexnowResult && (
                    <p className="text-sm text-green-700 mt-3">Pushed {indexnowResult.pushed} of {indexnowResult.total} URLs ({indexnowResult.static_count} pages + {indexnowResult.blog_count} posts).</p>
                  )}
                </div>

                {/* Interlink pass */}
                <div className="bg-white rounded-lg shadow p-5" data-testid="seo-interlink-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="font-semibold text-gray-900">Run internal-linking pass</h3>
                      <p className="text-sm text-gray-600 mt-1">Scans every published post and adds pillar links (Pro Plan, Authority Hub, /book, /websites-apps) plus cross-post links. Idempotent, safe to re-run.</p>
                    </div>
                    <button
                      onClick={doInterlink}
                      disabled={seoBusy}
                      className="inline-flex items-center gap-2 bg-purple-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50"
                      data-testid="seo-interlink-run"
                    >
                      <RefreshCw className={`w-4 h-4 ${seoBusy ? 'animate-spin' : ''}`} /> Run pass
                    </button>
                  </div>
                  {interlinkResult && (
                    <p className="text-sm text-green-700 mt-3">Updated {interlinkResult.updated} of {interlinkResult.total_posts} posts.</p>
                  )}
                </div>

                {/* Google Search Console: Weekly Quick Wins */}
                <div className="bg-white rounded-lg shadow p-5" data-testid="seo-gsc-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="font-semibold text-gray-900">Search Console Quick Wins</h3>
                      <p className="text-sm text-gray-600 mt-1">The 15-min weekly ritual. Pages currently ranking position 5-20 with CTR below 3%, these are the fastest wins in the whole SEO plan.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={gscDays}
                        onChange={(e) => { const d = Number(e.target.value); setGscDays(d); fetchGsc(d); }}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                        data-testid="gsc-range"
                      >
                        <option value={7}>Last 7 days</option>
                        <option value={28}>Last 28 days</option>
                        <option value={90}>Last 90 days</option>
                      </select>
                      <button
                        onClick={() => fetchGsc(gscDays)}
                        disabled={gscLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                        data-testid="gsc-refresh"
                      >
                        <RefreshCw className={`w-4 h-4 ${gscLoading ? 'animate-spin' : ''}`} /> Refresh
                      </button>
                    </div>
                  </div>

                  {gscLoading && !gsc && <p className="text-sm text-gray-500 mt-4">Loading Search Console data…</p>}

                  {gsc && !gsc.ok && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                      <b>GSC error:</b> {gsc.error}. Make sure the service account is added as a Full user on the property.
                    </div>
                  )}

                  {gsc && gsc.ok && (
                    <div className="mt-5 space-y-6">
                      {/* Totals */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Impressions</p>
                          <p className="text-2xl font-bold text-gray-900 mt-1">{gsc.totals.impressions.toLocaleString()}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Clicks</p>
                          <p className="text-2xl font-bold text-gray-900 mt-1">{gsc.totals.clicks.toLocaleString()}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Avg CTR</p>
                          <p className="text-2xl font-bold text-gray-900 mt-1">{(gsc.totals.ctr * 100).toFixed(2)}%</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Property</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1 break-all">{gsc.site}</p>
                          <p className="text-[11px] text-gray-500">{gsc.start_date} → {gsc.end_date}</p>
                        </div>
                      </div>

                      {/* Quick wins */}
                      <div>
                        <h4 className="font-semibold text-gray-900">🎯 Quick-win queries ({gsc.quick_wins.length})</h4>
                        <p className="text-xs text-gray-500 mb-2">Position 5-20, CTR &lt; 3%, at least 20 impressions. Rewrite the ranking page's title/meta to hoover these up.</p>
                        {gsc.quick_wins.length === 0 ? (
                          <p className="text-sm text-gray-500 italic">No quick wins yet in this window. Keep publishing.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                                <tr>
                                  <th className="text-left px-3 py-2">Query</th>
                                  <th className="text-right px-3 py-2">Impressions</th>
                                  <th className="text-right px-3 py-2">Clicks</th>
                                  <th className="text-right px-3 py-2">CTR</th>
                                  <th className="text-right px-3 py-2">Avg Position</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {gsc.quick_wins.map((r, i) => (
                                  <tr key={i} data-testid={`gsc-qw-${i}`}>
                                    <td className="px-3 py-2 font-medium text-gray-900">{r.query}</td>
                                    <td className="px-3 py-2 text-right">{r.impressions.toLocaleString()}</td>
                                    <td className="px-3 py-2 text-right">{r.clicks}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-orange-600">{(r.ctr * 100).toFixed(2)}%</td>
                                    <td className="px-3 py-2 text-right">{r.position.toFixed(1)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      {/* Wrong-topic detector */}
                      {gsc.wrong_topic_queries.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900">⚠️ Wrong-topic queries ({gsc.wrong_topic_queries.length})</h4>
                          <p className="text-xs text-gray-500 mb-2">Queries containing &ldquo;audio&rdquo; but not any of our real topics. Click <b>Fix this</b> on any row to see the exact page ranking for it, then rewrite the title/meta or unpublish in one click.</p>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                                <tr>
                                  <th className="text-left px-3 py-2">Query</th>
                                  <th className="text-right px-3 py-2">Impressions</th>
                                  <th className="text-right px-3 py-2">Position</th>
                                  <th className="text-right px-3 py-2">Action</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {gsc.wrong_topic_queries.map((r, i) => {
                                  const exp = wtExpanded[r.query];
                                  return (
                                    <React.Fragment key={i}>
                                      <tr className="bg-red-50/40">
                                        <td className="px-3 py-2 text-gray-900">{r.query}</td>
                                        <td className="px-3 py-2 text-right">{r.impressions.toLocaleString()}</td>
                                        <td className="px-3 py-2 text-right">{r.position.toFixed(1)}</td>
                                        <td className="px-3 py-2 text-right">
                                          <button
                                            onClick={() => toggleWrongTopicPages(r.query)}
                                            className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 text-xs font-semibold underline"
                                            data-testid={`wt-fix-${i}`}
                                          >
                                            {exp?.open ? 'Hide' : 'Fix this →'}
                                          </button>
                                        </td>
                                      </tr>
                                      {exp?.open && (
                                        <tr>
                                          <td colSpan={4} className="bg-red-50/70 px-4 py-3 border-t border-red-200">
                                            {exp.loading && <p className="text-xs text-gray-600">Loading pages…</p>}
                                            {exp.error && <p className="text-xs text-red-700">Couldn&rsquo;t load pages: {exp.error}</p>}
                                            {exp.pages && exp.pages.length === 0 && (
                                              <p className="text-xs text-gray-600">No pages returned by GSC for this exact query in the last 90 days. Try widening the timeframe in Search Console directly.</p>
                                            )}
                                            {exp.pages && exp.pages.length > 0 && (
                                              <div className="space-y-2">
                                                <p className="text-xs font-semibold text-gray-700">Pages ranking on <span className="italic">&ldquo;{r.query}&rdquo;</span>:</p>
                                                <ul className="space-y-2">
                                                  {exp.pages.map((p, j) => {
                                                    const slug = pageToSlug(p.page);
                                                    return (
                                                      <li key={j} className="bg-white border border-red-100 rounded p-2 flex flex-wrap items-center justify-between gap-2">
                                                        <div className="min-w-0 flex-1">
                                                          <a href={p.page} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 underline break-all">{p.page.replace('https://audioandco.com', '')}</a>
                                                          <div className="text-[11px] text-gray-500 mt-0.5">{p.impressions} imps · {p.clicks} clicks · pos {Number(p.position || 0).toFixed(1)}</div>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 flex-shrink-0">
                                                          {slug ? (
                                                            <>
                                                              <button
                                                                onClick={() => rewritePageBySlug(slug)}
                                                                className="inline-flex items-center gap-1 bg-red-600 text-white text-xs font-semibold px-2.5 py-1 rounded hover:bg-red-700"
                                                                data-testid={`wt-rewrite-${i}-${j}`}
                                                              >
                                                                Rewrite title/meta
                                                              </button>
                                                              <button
                                                                onClick={() => unpublishBySlug(slug)}
                                                                className="inline-flex items-center gap-1 bg-white border border-red-400 text-red-700 text-xs font-semibold px-2.5 py-1 rounded hover:bg-red-50"
                                                                data-testid={`wt-unpublish-${i}-${j}`}
                                                              >
                                                                Unpublish
                                                              </button>
                                                            </>
                                                          ) : (
                                                            <span className="text-[11px] text-gray-500 italic">Not a blog post (edit page manually)</span>
                                                          )}
                                                        </div>
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Top by clicks */}
                      <div>
                        <h4 className="font-semibold text-gray-900">🚀 Top performing queries</h4>
                        <p className="text-xs text-gray-500 mb-2">What&rsquo;s already earning clicks. Double down here.</p>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                              <tr>
                                <th className="text-left px-3 py-2">Query</th>
                                <th className="text-right px-3 py-2">Clicks</th>
                                <th className="text-right px-3 py-2">Impressions</th>
                                <th className="text-right px-3 py-2">CTR</th>
                                <th className="text-right px-3 py-2">Position</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {gsc.top_by_clicks.map((r, i) => (
                                <tr key={i}>
                                  <td className="px-3 py-2 font-medium text-gray-900">{r.query}</td>
                                  <td className="px-3 py-2 text-right font-semibold text-green-700">{r.clicks}</td>
                                  <td className="px-3 py-2 text-right">{r.impressions.toLocaleString()}</td>
                                  <td className="px-3 py-2 text-right">{(r.ctr * 100).toFixed(2)}%</td>
                                  <td className="px-3 py-2 text-right">{r.position.toFixed(1)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Silent pages */}
                      {gsc.silent_pages.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900">🔇 Silent pages ({gsc.silent_pages.length})</h4>
                          <p className="text-xs text-gray-500 mb-2">Pages showing 50+ impressions but zero clicks. Rewrite the title/meta to convert impressions into clicks.</p>
                          <ul className="space-y-1.5">
                            {gsc.silent_pages.map((r, i) => {
                              const slug = pageToSlug(r.page);
                              return (
                                <li key={i} className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 rounded p-2">
                                  <div className="min-w-0 flex-1">
                                    <a href={r.page} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 underline break-all">{r.page.replace('https://audioandco.com', '')}</a>
                                    <span className="text-xs text-gray-500 ml-2">({r.impressions.toLocaleString()} imps @ pos {r.position.toFixed(1)})</span>
                                  </div>
                                  {slug && (
                                    <button
                                      onClick={() => rewritePageBySlug(slug)}
                                      className="inline-flex items-center gap-1 bg-red-600 text-white text-xs font-semibold px-2.5 py-1 rounded hover:bg-red-700 flex-shrink-0"
                                      data-testid={`silent-rewrite-${i}`}
                                    >
                                      Rewrite title/meta
                                    </button>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Content Sprint card removed Feb 2026: daily auto-generator
                    now covers steady output, and batching 10 posts at once
                    burned LLM credits for marginal ranking lift. */}
                <div className="bg-white rounded-lg shadow p-5 border-2 border-amber-400" data-testid="seo-heading-fix-card">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-amber-500 rounded-full"></span>
                        Fix broken blog headings
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Walks every blog post and cleans up any heading where the AI wrote literal "H3:" text or used the wrong level. Also promotes section headings to H2 (best for SEO). Safe to run any time; only touches posts that actually need fixing.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => doHeadingFix(true)}
                      disabled={headingFixBusy}
                      className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      data-testid="heading-fix-dryrun"
                    >
                      {headingFixBusy ? 'Checking...' : 'Preview (dry-run)'}
                    </button>
                    <button
                      onClick={() => doHeadingFix(false)}
                      disabled={headingFixBusy}
                      className="inline-flex items-center gap-2 bg-amber-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-amber-700 disabled:opacity-50"
                      data-testid="heading-fix-apply"
                    >
                      {headingFixBusy ? 'Fixing...' : 'Fix headings on all posts'}
                    </button>
                  </div>
                  {headingFixResult && (
                    <div className={`mt-4 p-4 rounded-lg border-2 ${headingFixResult.updated > 0 ? 'bg-green-50 border-green-500' : 'bg-gray-50 border-gray-300'}`} data-testid="heading-fix-result">
                      <p className="text-sm font-semibold text-gray-900">
                        {headingFixResult.dry_run ? 'Preview:' : 'Done.'}{' '}
                        {headingFixResult.updated} of {headingFixResult.scanned} posts {headingFixResult.dry_run ? 'would be updated' : 'updated'}
                      </p>
                      {headingFixResult.posts && headingFixResult.posts.length > 0 && (
                        <ul className="mt-2 text-xs text-gray-700 max-h-40 overflow-auto space-y-0.5">
                          {headingFixResult.posts.map((p, i) => (
                            <li key={i} className="font-mono">{p.slug}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                {/* Deep content quality sweep */}
                <div className="bg-white rounded-lg shadow p-5 border-2 border-purple-400" data-testid="seo-quality-sweep-card">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-purple-500 rounded-full"></span>
                    Deep content quality sweep
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Scans every blog post for AI-fingerprint patterns that Google may be penalising:
                  </p>
                  <ul className="mt-2 text-sm text-gray-700 list-disc pl-5 space-y-0.5">
                    <li>Em-dashes converted to commas</li>
                    <li>"Imagine you are a coach..." rewritten to "Say you're a coach..."</li>
                    <li>"In today's fast-paced world," / "In conclusion," openers stripped</li>
                    <li>Literal "H3:" text converted to real Markdown headings</li>
                    <li>Posts with fake named clients ("Let's call her Sarah") or vague "I worked with a client" references are <b>unpublished</b> so Google stops crawling them until you rewrite manually</li>
                  </ul>
                  <p className="text-xs text-gray-500 mt-2">No LLM cost. Pure deterministic pass. Safe to run any time.</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => doQualitySweep(true, true)}
                      disabled={qualityBusy}
                      className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      data-testid="quality-sweep-dryrun"
                    >
                      {qualityBusy ? 'Checking...' : 'Preview (dry-run)'}
                    </button>
                    <button
                      onClick={() => doQualitySweep(false, true)}
                      disabled={qualityBusy}
                      className="inline-flex items-center gap-2 bg-purple-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50"
                      data-testid="quality-sweep-apply"
                    >
                      {qualityBusy ? 'Sweeping...' : 'Run full quality sweep'}
                    </button>
                  </div>
                  {qualityResult && (
                    <div className="mt-4 p-4 bg-purple-50 border-2 border-purple-500 rounded-lg" data-testid="quality-sweep-result">
                      <p className="text-sm font-semibold text-gray-900">
                        {qualityResult.dry_run ? 'Preview:' : 'Done.'} Scanned {qualityResult.scanned} posts.
                      </p>
                      <div className="mt-2 grid sm:grid-cols-2 gap-3 text-sm">
                        <div className="bg-white rounded p-3 border border-purple-200">
                          <div className="font-semibold text-purple-900">Cleaned</div>
                          <div className="text-2xl font-bold">{qualityResult.content_updated}</div>
                          <div className="text-xs text-gray-500">posts with safe fixes applied</div>
                        </div>
                        <div className="bg-white rounded p-3 border border-purple-200">
                          <div className="font-semibold text-red-800">Unpublished</div>
                          <div className="text-2xl font-bold">{qualityResult.unpublished}</div>
                          <div className="text-xs text-gray-500">flagged for your manual review</div>
                        </div>
                      </div>
                      {qualityResult.unpublished_posts && qualityResult.unpublished_posts.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs font-semibold text-red-800 mb-1">Unpublished posts (need your rewrite):</p>
                          <ul className="text-xs text-gray-700 max-h-40 overflow-auto space-y-1 bg-white rounded p-2 border border-purple-200">
                            {qualityResult.unpublished_posts.map((p, i) => (
                              <li key={i} className="font-mono">
                                <span className="text-red-700">/blog/{p.slug}</span>
                                <div className="text-gray-500 pl-3">{(p.risky_reasons || []).join(', ')}</div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Blog image dedupe */}
                <div className="bg-white rounded-lg shadow p-5 border-2 border-teal-400" data-testid="seo-image-dedupe-card">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-teal-500 rounded-full"></span>
                    Fix duplicate blog images
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Ensures every published blog post has a unique, topic-matched featured image with proper alt text and photographer credit. Pulls from a curated Unsplash pool of 40+ podcast, publishing, SEO and business photos. Zero LLM cost.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => doDedupeImages(true)}
                      disabled={imgBusy}
                      className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      data-testid="image-dedupe-dryrun"
                    >
                      {imgBusy ? 'Checking...' : 'Preview (dry-run)'}
                    </button>
                    <button
                      onClick={() => doDedupeImages(false)}
                      disabled={imgBusy}
                      className="inline-flex items-center gap-2 bg-teal-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-teal-700 disabled:opacity-50"
                      data-testid="image-dedupe-apply"
                    >
                      {imgBusy ? 'Assigning...' : 'Assign unique images'}
                    </button>
                  </div>
                  {imgResult && (
                    <div className="mt-4 p-4 bg-teal-50 border-2 border-teal-500 rounded-lg" data-testid="image-dedupe-result">
                      <p className="text-sm font-semibold text-gray-900">
                        {imgResult.dry_run ? 'Preview:' : 'Done.'} {imgResult.updated} of {imgResult.scanned} posts {imgResult.dry_run ? 'would be updated' : 'updated'}
                      </p>
                      {imgResult.updated_posts && imgResult.updated_posts.length > 0 && (
                        <ul className="mt-2 text-xs text-gray-700 max-h-48 overflow-auto space-y-1 bg-white rounded p-2 border border-teal-200">
                          {imgResult.updated_posts.map((u, i) => (
                            <li key={i} className="font-mono">
                              <span className="text-teal-800">/blog/{u.slug}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                {/* Rebase blog dates card removed Feb 2026: one-off job
                    completed, all dates now unique. */}
                <div className="bg-white rounded-lg shadow p-5 border-2 border-orange-400" data-testid="seo-promise-check-card">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-orange-500 rounded-full"></span>
                    Title-promise check
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Finds posts where the title promises "5 steps", "3 mistakes", "7 tips" etc. but the body doesn't deliver that many. Failing posts are auto-unpublished. Prevents bounce-back to Google. Zero content changes.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => doPromiseCheck(true)}
                      disabled={promiseBusy}
                      className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      data-testid="promise-check-dryrun"
                    >
                      {promiseBusy ? 'Checking...' : 'Preview (dry-run)'}
                    </button>
                    <button
                      onClick={() => doPromiseCheck(false)}
                      disabled={promiseBusy}
                      className="inline-flex items-center gap-2 bg-orange-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-orange-700 disabled:opacity-50"
                      data-testid="promise-check-apply"
                    >
                      {promiseBusy ? 'Sweeping...' : 'Unpublish overpromising posts'}
                    </button>
                  </div>
                  {promiseResult && (
                    <div className="mt-4 p-4 bg-orange-50 border-2 border-orange-500 rounded-lg" data-testid="promise-check-result">
                      <p className="text-sm font-semibold text-gray-900">
                        {promiseResult.dry_run ? 'Preview:' : 'Done.'} {promiseResult.failing} of {promiseResult.scanned} posts fail their title promise.
                      </p>
                      {promiseResult.failing_posts && promiseResult.failing_posts.length > 0 && (
                        <ul className="mt-2 text-xs text-gray-700 max-h-56 overflow-auto space-y-1 bg-white rounded p-2 border border-orange-200">
                          {promiseResult.failing_posts.map((u, i) => (
                            <li key={i} className="font-mono">
                              <span className="text-orange-800">/blog/{u.slug}</span>
                              <div className="text-gray-600 pl-2">Title promised <b>{u.promised} {u.kind}s</b>, body has {u.found}</div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                {/* Sitemap resubmit (Google + Bing/Yandex in one click) */}
                <div className="bg-white rounded-lg shadow p-5 border-2 border-purple-400" data-testid="seo-resubmit-sitemap-card">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-purple-500 rounded-full"></span>
                    Resubmit sitemap (Google + Bing + Yandex)
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    One click: regenerates <span className="font-mono text-xs">/sitemap.xml</span>, submits it to Google Search Console, and pings IndexNow so Bing, Yandex, DuckDuckGo and Seznam re-crawl every current URL (static pages, guides, blog posts, hubs). Use after any bulk content change.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={doResubmitSitemap}
                      disabled={resubmitBusy}
                      className="inline-flex items-center gap-2 bg-purple-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-purple-700 disabled:opacity-50"
                      data-testid="resubmit-sitemap-btn"
                    >
                      {resubmitBusy ? 'Resubmitting...' : 'Regenerate & resubmit sitemap'}
                    </button>
                  </div>
                  {resubmitResult && (
                    <div className="mt-4 p-4 bg-purple-50 border-2 border-purple-500 rounded-lg text-sm text-gray-800 space-y-2" data-testid="resubmit-sitemap-result">
                      <p><b>Sitemap:</b> {resubmitResult.sitemap?.ok ? 'regenerated' : 'FAILED'}</p>
                      <p><b>Google Search Console:</b> {resubmitResult.google_search_console?.ok ? 'submitted' : `skipped (${resubmitResult.google_search_console?.error?.slice(0, 80) || 'no creds'})`}</p>
                      <p><b>IndexNow:</b> pushed {resubmitResult.indexnow?.pushed} of {resubmitResult.indexnow?.total} URLs ({resubmitResult.indexnow?.static_count} static + {resubmitResult.indexnow?.blog_count} blogs)</p>
                      {resubmitResult.note && <p className="text-xs text-gray-500 pt-2 border-t border-purple-200">{resubmitResult.note}</p>}
                    </div>
                  )}
                </div>

                {/* Hub interlink sweep */}
                <div className="bg-white rounded-lg shadow p-5 border-2 border-emerald-400" data-testid="seo-interlink-hubs-card">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full"></span>
                    Hub interlink sweep
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Adds a "Read the full guide" callout to every published blog post, linking to its best-matching topic-cluster hub (podcast launching, podcast management, book publishing, Authority Hub, SEO, content repurposing, podcast production). This is the missing link that turns 70+ scattered posts into a hub-and-spoke Google reads as topical authority. Idempotent, safe to re-run.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => doInterlinkHubs(true)}
                      disabled={hubInterlinkBusy}
                      className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      data-testid="interlink-hubs-dryrun"
                    >
                      {hubInterlinkBusy ? 'Checking...' : 'Preview (dry-run)'}
                    </button>
                    <button
                      onClick={() => doInterlinkHubs(false)}
                      disabled={hubInterlinkBusy}
                      className="inline-flex items-center gap-2 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                      data-testid="interlink-hubs-apply"
                    >
                      {hubInterlinkBusy ? 'Injecting...' : 'Inject hub callouts'}
                    </button>
                  </div>
                  {hubInterlinkResult && (
                    <div className="mt-4 p-4 bg-emerald-50 border-2 border-emerald-500 rounded-lg" data-testid="interlink-hubs-result">
                      <p className="text-sm font-semibold text-gray-900">
                        {hubInterlinkResult.dry_run ? 'Preview:' : 'Done.'} {hubInterlinkResult.updated} of {hubInterlinkResult.scanned} posts {hubInterlinkResult.dry_run ? 'would be' : 'were'} interlinked. {hubInterlinkResult.skipped} had no matching hub.
                      </p>
                      {hubInterlinkResult.posts && hubInterlinkResult.posts.length > 0 && (
                        <ul className="mt-2 text-xs text-gray-700 max-h-56 overflow-auto space-y-1 bg-white rounded p-2 border border-emerald-200">
                          {hubInterlinkResult.posts.map((u, i) => (
                            <li key={i} className="font-mono">
                              <span className="text-emerald-800">/blog/{u.slug}</span>
                              <div className="text-gray-600 pl-2">→ {u.hub_url}</div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>

                {/* Bulk SEO rewrite — batch fix worst 20 posts in one click */}
                <div className="bg-white rounded-lg shadow p-5 border-2 border-emerald-500" data-testid="seo-bulk-rewrite-card">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full"></span>
                    Batch-rewrite worst blog titles &amp; metas
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Runs the LLM title/meta rewriter across the <b>20 published posts that have never been SEO-rewritten</b> (oldest first). Auto-applies the best option, pings IndexNow, marks each post with <span className="font-mono text-xs">seo_rewritten_at</span> so this is safe to re-run. Estimated cost <b>~£0.02 of LLM credits</b>, time 5-10 minutes. The single biggest lever for boosting blog CTR.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => doBulkSeoRewrite({ dryRun: true })}
                      disabled={bulkSeoBusy}
                      className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      data-testid="bulk-seo-dryrun"
                    >
                      {bulkSeoBusy ? 'Checking...' : 'Preview (dry-run)'}
                    </button>
                    <button
                      onClick={() => doBulkSeoRewrite({ dryRun: false, limit: 20 })}
                      disabled={bulkSeoBusy}
                      className="inline-flex items-center gap-2 bg-emerald-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                      data-testid="bulk-seo-run-20"
                    >
                      {bulkSeoBusy ? 'Rewriting...' : 'Rewrite worst 20 posts'}
                    </button>
                    <button
                      onClick={() => doBulkSeoRewrite({ dryRun: false, limit: 5 })}
                      disabled={bulkSeoBusy}
                      className="inline-flex items-center gap-2 bg-white border border-emerald-500 text-emerald-700 font-semibold px-4 py-2 rounded-md hover:bg-emerald-50 disabled:opacity-50"
                      data-testid="bulk-seo-run-5"
                    >
                      Rewrite just 5 (safer test)
                    </button>
                  </div>
                  {bulkSeoResult && (
                    <div className="mt-4 p-4 bg-emerald-50 border-2 border-emerald-500 rounded-lg text-sm" data-testid="bulk-seo-result">
                      {bulkSeoResult.dry_run ? (
                        <>
                          <p className="font-semibold text-gray-900">Preview: {bulkSeoResult.would_rewrite} posts would be rewritten (oldest, never SEO-rewritten):</p>
                          <ul className="mt-2 space-y-1 text-xs text-gray-700 max-h-56 overflow-auto bg-white rounded p-2 border border-emerald-200">
                            {(bulkSeoResult.slugs || []).map((s, i) => (
                              <li key={i} className="font-mono">/blog/{s.slug} <span className="text-gray-500">— {s.current_title}</span></li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <>
                          <p className="font-semibold text-gray-900">
                            Rewrote {bulkSeoResult.rewritten} of {bulkSeoResult.scanned} posts ({bulkSeoResult.failed} failed).
                          </p>
                          <ul className="mt-2 space-y-1 text-xs text-gray-700 max-h-72 overflow-auto bg-white rounded p-2 border border-emerald-200">
                            {(bulkSeoResult.results || []).map((r, i) => (
                              <li key={i} className={r.ok ? 'text-emerald-800' : 'text-red-700'}>
                                <span className="font-mono">/blog/{r.slug}</span>
                                {r.ok ? <> ✓ <span className="text-gray-700">{r.new_title}</span></> : <> ✗ {r.reason}</>}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* FAQ injection */}
                <div className="bg-white rounded-lg shadow p-5 border-2 border-blue-400" data-testid="seo-inject-faqs-card">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                    Auto-inject 4 FAQs per blog post
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Runs the LLM once per post to generate 4 topic-relevant Q&amp;A pairs, checks each against the same voice/preflight bans as the main generator, stores them on the post, and renders both a visible FAQ section and Google/Bing FAQPage schema. Skips posts that already have FAQs (safe to re-run after new posts publish). Takes ~5-8 min for a full sweep of 70+ posts.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={() => doFaqInject({ dryRun: true, onlyMissing: true })}
                      disabled={faqInjectBusy}
                      className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 font-semibold px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
                      data-testid="inject-faqs-dryrun"
                    >
                      {faqInjectBusy ? 'Working...' : 'Preview (dry-run)'}
                    </button>
                    <button
                      onClick={() => doFaqInject({ dryRun: false, onlyMissing: true })}
                      disabled={faqInjectBusy}
                      className="inline-flex items-center gap-2 bg-blue-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                      data-testid="inject-faqs-apply"
                    >
                      {faqInjectBusy ? 'Injecting FAQs...' : 'Inject FAQs (missing only)'}
                    </button>
                    <button
                      onClick={() => window.confirm('Regenerate FAQs on ALL posts, replacing existing ones? This uses more LLM credits.') && doFaqInject({ dryRun: false, onlyMissing: false })}
                      disabled={faqInjectBusy}
                      className="inline-flex items-center gap-2 bg-white border border-blue-600 text-blue-700 font-semibold px-4 py-2 rounded-md hover:bg-blue-50 disabled:opacity-50"
                      data-testid="inject-faqs-all"
                    >
                      Regenerate all
                    </button>
                  </div>
                  {faqInjectResult && (
                    <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-500 rounded-lg" data-testid="inject-faqs-result">
                      <p className="text-sm font-semibold text-gray-900">
                        {faqInjectResult.dry_run ? 'Preview:' : 'Done.'} {faqInjectResult.updated} of {faqInjectResult.scanned} posts {faqInjectResult.dry_run ? 'would get' : 'got'} FAQs. {faqInjectResult.failed} failed.
                      </p>
                      {faqInjectResult.failed_posts && faqInjectResult.failed_posts.length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs text-red-700 cursor-pointer">Failed posts ({faqInjectResult.failed_posts.length})</summary>
                          <ul className="mt-1 text-xs text-gray-700 max-h-40 overflow-auto space-y-1 bg-white rounded p-2 border border-blue-200 font-mono">
                            {faqInjectResult.failed_posts.map((u, i) => (
                              <li key={i}>/blog/{u.slug} - {u.reason}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  )}
                </div>

                {/* Reddit Q&A blog generator */}
                <div className="bg-white rounded-lg shadow p-5" data-testid="seo-reddit-card">
                  <h3 className="font-semibold text-gray-900">Reddit Q&amp;A blog generator</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Pick a question people are actively asking on Reddit, publish a proper UK-focused answer under your own domain, ride the long-tail search traffic. Paste the Reddit URL and the question title, we do the rest.
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Great subreddits: <a href="https://www.reddit.com/r/podcasting/top/?t=month" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">r/podcasting</a>, <a href="https://www.reddit.com/r/smallbusiness/top/?t=month" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">r/smallbusiness</a>, <a href="https://www.reddit.com/r/coaching/top/?t=month" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">r/coaching</a>, <a href="https://www.reddit.com/r/entrepreneur/top/?t=month" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">r/entrepreneur</a>, <a href="https://www.reddit.com/r/selfpublish/top/?t=month" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">r/selfpublish</a>.
                  </p>
                  <div className="mt-4 space-y-3">
                    <input
                      type="text"
                      value={redditForm.title}
                      onChange={(e) => setRedditForm({ ...redditForm, title: e.target.value })}
                      placeholder="Paste the exact question title from Reddit (e.g. How do I get my podcast on Spotify with no audience?)"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reddit-title"
                    />
                    <div className="grid sm:grid-cols-2 gap-3">
                      <select
                        value={redditForm.subreddit}
                        onChange={(e) => setRedditForm({ ...redditForm, subreddit: e.target.value })}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                        data-testid="reddit-sub"
                      >
                        {['podcasting','podcasts','smallbusiness','entrepreneur','coaching','marketing','SEO','selfpublish','solopreneurs','startups'].map(s => (
                          <option key={s} value={s}>r/{s}</option>
                        ))}
                      </select>
                      <input
                        type="url"
                        value={redditForm.url}
                        onChange={(e) => setRedditForm({ ...redditForm, url: e.target.value })}
                        placeholder="Reddit post URL (optional, used for attribution)"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        data-testid="reddit-url"
                      />
                    </div>
                    <textarea
                      value={redditForm.selftext}
                      onChange={(e) => setRedditForm({ ...redditForm, selftext: e.target.value })}
                      placeholder="Extra context from the post body (optional). Copy-paste the OP's message if it adds detail."
                      rows={3}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reddit-selftext"
                    />
                    <button
                      onClick={doRedditGenerate}
                      disabled={redditBusy || !redditForm.title || !redditForm.subreddit}
                      className="inline-flex items-center gap-2 bg-red-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                      data-testid="reddit-generate"
                    >
                      <Zap className={`w-4 h-4 ${redditBusy ? 'animate-pulse' : ''}`} /> {redditBusy ? 'Writing...' : 'Generate & publish blog'}
                    </button>
                  </div>
                  {redditResult && redditResult.ok && (
                    <div className="mt-4 p-4 bg-green-50 border-2 border-green-500 rounded-lg" data-testid="reddit-generate-success">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-500 text-white font-bold flex items-center justify-center flex-shrink-0">✓</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-green-900">Blog post published and live</p>
                          <p className="text-base font-bold text-gray-900 mt-1 break-words">{redditResult.title}</p>
                          <p className="text-xs text-gray-500 mt-1 break-all">Slug: <code>/blog/{redditResult.slug}</code></p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <a
                              href={`${window.location.origin}/blog/${redditResult.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 bg-green-600 text-white text-sm font-semibold px-3 py-1.5 rounded hover:bg-green-700"
                              data-testid="reddit-view-post"
                            >
                              View the new post →
                            </a>
                            <a
                              href={`${window.location.origin}/blog`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-semibold px-3 py-1.5 rounded hover:bg-gray-50"
                            >
                              Open blog index
                            </a>
                          </div>
                          <p className="text-xs text-gray-500 mt-3">IndexNow pinged. Bing typically picks it up within minutes; Google can take a few hours.</p>
                        </div>
                      </div>
                    </div>
                  )}
                  {redditResult && !redditResult.ok && (
                    <div className="mt-4 p-3 bg-red-50 border-2 border-red-400 rounded-lg text-sm text-red-800" data-testid="reddit-generate-error">
                      <p className="font-semibold">Generation failed</p>
                      <p className="mt-1">{redditResult.error || 'Unknown error, check backend logs.'}</p>
                    </div>
                  )}
                </div>

                {/* Bing Webmaster: mirror of GSC panel */}
                <div className="bg-white rounded-lg shadow p-5" data-testid="seo-bing-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h3 className="font-semibold text-gray-900">Bing / Copilot Quick Wins</h3>
                      <p className="text-sm text-gray-600 mt-1">Bing powers Copilot and ChatGPT-search results. Data is trailing ~6 months (Bing doesn&rsquo;t support date ranges).</p>
                    </div>
                    <button
                      onClick={fetchBing}
                      disabled={bingLoading}
                      className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                      data-testid="bing-refresh"
                    >
                      <RefreshCw className={`w-4 h-4 ${bingLoading ? 'animate-spin' : ''}`} /> Refresh
                    </button>
                  </div>

                  {bingLoading && !bing && <p className="text-sm text-gray-500 mt-4">Loading Bing data…</p>}
                  {bing && !bing.ok && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                      <b>Bing error:</b> {bing.error}
                    </div>
                  )}
                  {bing && bing.ok && (
                    <div className="mt-5 space-y-6">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Impressions</p>
                          <p className="text-2xl font-bold text-gray-900 mt-1">{bing.totals.impressions.toLocaleString()}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Clicks</p>
                          <p className="text-2xl font-bold text-gray-900 mt-1">{bing.totals.clicks.toLocaleString()}</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Avg CTR</p>
                          <p className="text-2xl font-bold text-gray-900 mt-1">{(bing.totals.ctr * 100).toFixed(2)}%</p>
                        </div>
                        <div className="bg-gray-50 rounded p-3">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Property</p>
                          <p className="text-sm font-semibold text-gray-900 mt-1 break-all">{bing.site}</p>
                        </div>
                      </div>

                      {bing.top_by_impressions.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900">🔍 Top Bing queries by impressions</h4>
                          <div className="overflow-x-auto mt-2">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                                <tr>
                                  <th className="text-left px-3 py-2">Query</th>
                                  <th className="text-right px-3 py-2">Impressions</th>
                                  <th className="text-right px-3 py-2">Clicks</th>
                                  <th className="text-right px-3 py-2">Position</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {bing.top_by_impressions.map((r, i) => (
                                  <tr key={i} data-testid={`bing-q-${i}`}>
                                    <td className="px-3 py-2 font-medium text-gray-900">{r.query}</td>
                                    <td className="px-3 py-2 text-right">{r.impressions.toLocaleString()}</td>
                                    <td className="px-3 py-2 text-right">{r.clicks}</td>
                                    <td className="px-3 py-2 text-right">{r.position.toFixed(1)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {bing.wrong_topic_queries.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900">⚠️ Bing wrong-topic queries ({bing.wrong_topic_queries.length})</h4>
                          <div className="overflow-x-auto mt-2">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                                <tr>
                                  <th className="text-left px-3 py-2">Query</th>
                                  <th className="text-right px-3 py-2">Impressions</th>
                                  <th className="text-right px-3 py-2">Position</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {bing.wrong_topic_queries.map((r, i) => (
                                  <tr key={i} className="bg-red-50/40">
                                    <td className="px-3 py-2 text-gray-900">{r.query}</td>
                                    <td className="px-3 py-2 text-right">{r.impressions.toLocaleString()}</td>
                                    <td className="px-3 py-2 text-right">{r.position.toFixed(1)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {bing.silent_pages.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900">🔇 Bing silent pages ({bing.silent_pages.length})</h4>
                          <ul className="space-y-1 mt-2">
                            {bing.silent_pages.map((r, i) => (
                              <li key={i} className="text-sm">
                                <a href={r.page} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline break-all">{(r.page || '').replace('https://audioandco.com', '')}</a>
                                <span className="text-gray-500 ml-2">({r.impressions.toLocaleString()} imps @ pos {r.position.toFixed(1)})</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {bing.totals.impressions < 50 && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                          Bing footprint is small right now, this is normal for a freshly verified property. Bing typically catches up within 4-6 weeks of verification plus a sitemap submission. The IndexNow pings we send automatically will accelerate this.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Title / meta rewriter */}
                <div className="bg-white rounded-lg shadow p-5" data-testid="seo-rewriter-card">
                  <h3 className="font-semibold text-gray-900">Title &amp; meta rewriter (LLM-assisted)</h3>
                  <p className="text-sm text-gray-600 mt-1">Pick a blog post, get 3 UK-optimised title + meta description options, pick the winner. Applies instantly and re-pings IndexNow.</p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <select
                      value={seoSlug}
                      onChange={(e) => { setSeoSlug(e.target.value); setSeoOptions([]); }}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                      data-testid="seo-post-select"
                    >
                      <option value="">Pick a blog post to rewrite…</option>
                      {blogPosts.map((p) => (
                        <option key={p.slug} value={p.slug}>{p.title}</option>
                      ))}
                    </select>
                    <button
                      onClick={doSeoRewrite}
                      disabled={!seoSlug || seoBusy}
                      className="inline-flex items-center gap-2 bg-red-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                      data-testid="seo-rewrite-get"
                    >
                      <Zap className="w-4 h-4" /> {seoBusy ? 'Thinking…' : 'Get 3 options'}
                    </button>
                  </div>
                  {seoOptions.length > 0 && (
                    <div className="mt-5 space-y-3" data-testid="seo-rewrite-options">
                      {seoOptions.map((opt, i) => (
                        <div key={i} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Option {i + 1}</p>
                          <p className="font-semibold text-gray-900 mt-1">{opt.title}<span className="text-xs text-gray-500 ml-2">({(opt.title || '').length} chars)</span></p>
                          <p className="text-sm text-gray-700 mt-2 leading-relaxed">{opt.meta}<span className="text-xs text-gray-500 ml-2">({(opt.meta || '').length} chars)</span></p>
                          {opt.why && <p className="text-xs text-gray-500 mt-2 italic">{opt.why}</p>}
                          <button
                            onClick={() => applySeoRewrite(opt)}
                            className="mt-3 inline-flex items-center gap-1.5 bg-green-600 text-white text-sm font-semibold px-3 py-1.5 rounded hover:bg-green-700"
                            data-testid={`seo-rewrite-apply-${i}`}
                          >
                            <CheckCircle className="w-4 h-4" /> Apply this one
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* "All integrations connected" banner removed Feb 2026:
                    duplicates the status grid at the top of this tab. */}
              </div>
            )}


            {activeTab === 'leads' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Lead Management</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={fetchDashboardData}
                      className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                    >
                      Refresh
                    </button>
                    <button
                      onClick={() => exportToCSV([...leads, ...downloadConsents], 'leads')}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export to CSV
                    </button>
                  </div>
                </div>

                {/* Leads from LeadCaptureModal */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-3 bg-gray-100 border-b">
                    <h3 className="font-semibold text-gray-900">Lead Capture Modal Submissions ({leads.length})</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Company
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Services
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Consent
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {leads.map((lead) => (
                          <tr key={lead.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-semibold text-gray-900">{lead.name}</p>
                                <p className="text-sm text-gray-600">{lead.email}</p>
                                {lead.phone && <p className="text-sm text-gray-500">{lead.phone}</p>}
                                {lead.discovery_call && (
                                  <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-semibold mt-1" data-testid={`discovery-call-badge-${lead.id}`}>
                                    Discovery Call Requested
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">
                              {lead.company || '-'}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-1">
                                {(lead.services_interested || []).map((service, idx) => (
                                  <span key={idx} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                    {service}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col gap-1">
                                {lead.gdpr_consent && (
                                  <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                    GDPR ✓
                                  </span>
                                )}
                                {lead.marketing_consent && (
                                  <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                                    Marketing ✓
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <select
                                value={lead.status || 'new'}
                                onChange={(e) => updateStatus(lead.id, e.target.value, 'lead')}
                                className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${getStatusColor(lead.status || 'new')}`}
                              >
                                <option value="new">NEW</option>
                                <option value="contacted">CONTACTED</option>
                                <option value="pending">PENDING</option>
                                <option value="closed">CLOSED</option>
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(lead.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button 
                                onClick={() => viewDetails(lead, 'lead')}
                                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                              >
                                <Eye className="w-4 h-4 inline mr-1" />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {leads.length === 0 && (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No modal leads captured yet</p>
                    </div>
                  )}
                </div>

                {/* Newsletter Signups from FreeDownloadWidget */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-6 py-3 bg-gray-100 border-b">
                    <h3 className="font-semibold text-gray-900">Newsletter Signups ({downloadConsents.length})</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Consent
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {downloadConsents.map((consent) => (
                          <tr key={consent.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-semibold text-gray-900">
                                  {consent.first_name} {consent.surname}
                                </p>
                                <p className="text-sm text-gray-600">{consent.email}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col gap-1">
                                {consent.agreed_to_terms && (
                                  <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                                    Terms ✓
                                  </span>
                                )}
                                {consent.agreed_to_marketing && (
                                  <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                                    Marketing ✓
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <select
                                value={consent.status || 'new'}
                                onChange={(e) => updateStatus(consent.id, e.target.value, 'consent')}
                                className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${getStatusColor(consent.status || 'new')}`}
                              >
                                <option value="new">NEW</option>
                                <option value="contacted">CONTACTED</option>
                                <option value="pending">PENDING</option>
                                <option value="closed">CLOSED</option>
                              </select>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(consent.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button 
                                onClick={() => viewDetails(consent, 'download_consent')}
                                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                              >
                                <Eye className="w-4 h-4 inline mr-1" />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {downloadConsents.length === 0 && (
                    <div className="text-center py-8">
                      <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No newsletter signups yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'contacts' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Contact Inquiries</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={fetchDashboardData}
                      className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                    >
                      Refresh
                    </button>
                    <button
                      onClick={() => exportToCSV(contacts, 'contacts')}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export to CSV
                    </button>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Service
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Budget
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {contacts.map((contact) => (
                          <tr key={contact.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div>
                                <p className="font-semibold text-gray-900">{contact.name}</p>
                                <p className="text-sm text-gray-600">{contact.email}</p>
                                {contact.phone && <p className="text-sm text-gray-500">{contact.phone}</p>}
                                {contact.company && <p className="text-sm text-gray-500">{contact.company}</p>}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                {contact.service_category}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {contact.project_budget || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(contact.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button 
                                onClick={() => viewDetails(contact, 'contact')}
                                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                              >
                                <Eye className="w-4 h-4 inline mr-1" />
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {contacts.length === 0 && (
                    <div className="text-center py-8">
                      <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No contact inquiries yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'estimator' && <EstimatorLeadsPanel />}

            {activeTab === 'rankings' && <RankingsPanel />}

            {activeTab === 'audits' && <AuditsPanel />}

            {activeTab === 'drip' && (
              <div className="space-y-6" data-testid="admin-drip-tab">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Welcome Sequence</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      4-email onboarding for newsletter subscribers. Day 0 fires the instant someone signs up. Day 3, Day 10 and Day 21 are sent daily at 10:00 UTC via Brevo. Opens and clicks are tracked per stage.
                    </p>
                  </div>
                  <button
                    onClick={fetchDripStats}
                    disabled={dripLoading}
                    data-testid="drip-refresh"
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    {dripLoading ? 'Loading…' : 'Refresh'}
                  </button>
                </div>

                {!dripStats && !dripLoading && (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">No data loaded yet.</p>
                  </div>
                )}

                {dripStats && (
                  <>
                    {/* Top stats */}
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="bg-white rounded-xl p-5 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                          <Users className="w-5 h-5 text-red-600" />
                          <p className="text-sm font-semibold text-gray-600">Active Subscribers</p>
                        </div>
                        <p className="text-3xl font-black text-gray-900" data-testid="drip-total">{dripStats.total_active ?? 0}</p>
                        <p className="text-xs text-gray-500 mt-1">on the welcome sequence</p>
                      </div>
                      <div className="bg-white rounded-xl p-5 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                          <Send className="w-5 h-5 text-emerald-600" />
                          <p className="text-sm font-semibold text-gray-600">Day 0 Sent</p>
                        </div>
                        <p className="text-3xl font-black text-gray-900">{(dripStats.stage_counts && (dripStats.stage_counts['1'] ?? dripStats.stage_counts[1])) || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">&ldquo;Welcome, the honest version&rdquo;</p>
                      </div>
                      <div className="bg-white rounded-xl p-5 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                          <Send className="w-5 h-5 text-amber-600" />
                          <p className="text-sm font-semibold text-gray-600">Day 3 Sent</p>
                        </div>
                        <p className="text-3xl font-black text-gray-900">{(dripStats.stage_counts && (dripStats.stage_counts['2'] ?? dripStats.stage_counts[2])) || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">&ldquo;Pro Plan in 60 seconds&rdquo;</p>
                      </div>
                      <div className="bg-white rounded-xl p-5 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                          <Send className="w-5 h-5 text-blue-600" />
                          <p className="text-sm font-semibold text-gray-600">Day 10 Sent</p>
                        </div>
                        <p className="text-3xl font-black text-gray-900">{(dripStats.stage_counts && (dripStats.stage_counts['3'] ?? dripStats.stage_counts[3])) || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">&ldquo;Podcasts + YouTube + SEO&rdquo;</p>
                      </div>
                      <div className="bg-white rounded-xl p-5 shadow-sm">
                        <div className="flex items-center gap-3 mb-2">
                          <Send className="w-5 h-5 text-green-600" />
                          <p className="text-sm font-semibold text-gray-600">Day 21 Sent</p>
                        </div>
                        <p className="text-3xl font-black text-gray-900">{(dripStats.stage_counts && (dripStats.stage_counts['4'] ?? dripStats.stage_counts[4])) || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">&ldquo;Ready when you are&rdquo;</p>
                      </div>
                    </div>

                    {/* Engagement snapshot */}
                    <div className="bg-gradient-to-r from-red-50 to-amber-50 rounded-xl p-5 border border-red-100">
                      <div className="flex items-center gap-3 mb-3">
                        <Clock className="w-5 h-5 text-red-600" />
                        <h3 className="font-bold text-gray-900">Engagement (opens &amp; clicks per stage)</h3>
                      </div>
                      <div className="grid grid-cols-4 gap-3 text-center">
                        {['0','1','2','3'].map((k, i) => (
                          <div className="bg-white rounded-lg p-3" key={k}>
                            <div className="text-xs text-gray-500 uppercase tracking-wide">Day {[0,3,10,21][i]}</div>
                            <p className="text-lg font-black text-gray-900 mt-1">
                              {(dripStats.unique_opens_by_stage?.[k] ?? 0)} opens
                            </p>
                            <p className="text-xs text-red-600 font-semibold">
                              {(dripStats.unique_clicks_by_stage?.[k] ?? 0)} clicks
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent subscribers */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <div className="px-5 py-4 border-b border-gray-200">
                        <h3 className="font-bold text-gray-900">Recent Subscribers</h3>
                        <p className="text-xs text-gray-500 mt-1">Up to 30 most-recent signups + their sequence progress</p>
                      </div>
                      {(!dripStats.recent_subscribers || dripStats.recent_subscribers.length === 0) ? (
                        <div className="text-center py-12">
                          <Send className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500">No subscribers yet</p>
                          <p className="text-xs text-gray-400 mt-1">Day 0 fires the instant someone signs up via the footer/modal/blog form</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-xs text-gray-600 uppercase tracking-wide">
                              <tr>
                                <th className="px-4 py-3 text-left">Subscriber</th>
                                <th className="px-4 py-3 text-left">Source</th>
                                <th className="px-4 py-3 text-left">Signed up</th>
                                <th className="px-4 py-3 text-center">Stage</th>
                                <th className="px-4 py-3 text-center">Opens</th>
                                <th className="px-4 py-3 text-center">Clicks</th>
                                <th className="px-4 py-3 text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {dripStats.recent_subscribers.map((r) => (
                                <tr key={r.id} data-testid={`drip-row-${r.id}`}>
                                  <td className="px-4 py-3">
                                    <div className="font-semibold text-gray-900">{r.name || ','}</div>
                                    <div className="text-xs text-gray-500">{r.email}</div>
                                  </td>
                                  <td className="px-4 py-3 text-xs text-gray-600">{r.source || ','}</td>
                                  <td className="px-4 py-3 text-xs text-gray-600">{r.subscribed_at ? new Date(r.subscribed_at).toLocaleDateString('en-GB') : ','}</td>
                                  <td className="px-4 py-3 text-center text-xs font-semibold">
                                    {['New','Day 0 sent','Day 3 sent','Day 10 sent','Day 21 sent'][r.stage] || `Stage ${r.stage}`}
                                  </td>
                                  <td className="px-4 py-3 text-center text-xs">{r.opens_count ?? 0}</td>
                                  <td className="px-4 py-3 text-center text-xs">{r.clicks_count ?? 0}</td>
                                  <td className="px-4 py-3 text-center">
                                    {r.opt_out ? <span className="text-xs text-gray-500">Opted out</span>
                                              : <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-semibold">Active</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'emails' && (
              <div className="space-y-6" data-testid="admin-emails-tab">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Email Previews</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Read-only preview of every automated email leads receive. Ping me when you want
                      the wording tweaked, I'll update <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">welcome_email_templates.py</code>.
                    </p>
                  </div>
                  <button
                    onClick={fetchEmailPreviews}
                    disabled={emailLoading}
                    data-testid="email-refresh"
                    className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50 text-sm"
                  >
                    {emailLoading ? 'Loading…' : 'Refresh'}
                  </button>
                </div>

                {!emailPreviews && !emailLoading && (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <Mail className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-500">Click Refresh to load previews.</p>
                  </div>
                )}

                {emailLoading && (
                  <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                    <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-gray-500">Rendering previews…</p>
                  </div>
                )}

                {emailPreviews && (
                  <div className="grid grid-cols-12 gap-4">
                    {/* Left rail: list of email templates */}
                    <aside className="col-span-12 md:col-span-4 lg:col-span-3 space-y-2">
                      {emailPreviews.previews.map((p) => (
                        <button
                          key={p.key}
                          onClick={() => setSelectedEmailKey(p.key)}
                          data-testid={`email-tab-${p.key}`}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            selectedEmailKey === p.key
                              ? 'border-red-600 bg-red-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <div className="font-bold text-sm text-gray-900">{p.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5 leading-snug">{p.description}</div>
                        </button>
                      ))}
                      <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-gray-700 leading-relaxed border border-blue-100">
                        <strong className="text-blue-900">Sample data used:</strong><br />
                        Subscriber: {emailPreviews.sample_data.subscriber_name}<br />
                        Sequence: {(emailPreviews.sample_data.sequence_stages || []).join(' → ')}<br />
                        Scheduler: {emailPreviews.sample_data.scheduler_cron}
                      </div>
                    </aside>

                    {/* Right: selected email preview */}
                    <div className="col-span-12 md:col-span-8 lg:col-span-9">
                      {(() => {
                        const current = emailPreviews.previews.find(p => p.key === selectedEmailKey) || emailPreviews.previews[0];
                        if (!current) return null;
                        return (
                          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-200 bg-gray-50">
                              <div className="text-xs uppercase text-gray-500 tracking-wide font-bold mb-1">Subject line</div>
                              <div className="font-bold text-gray-900" data-testid="email-preview-subject">{current.subject}</div>
                              <div className="text-xs text-gray-500 mt-2">
                                <span className="font-bold">To:</span> {current.recipient}
                              </div>
                            </div>
                            <div className="px-5 py-3 border-b border-gray-200 bg-white flex flex-col sm:flex-row gap-2 sm:items-center">
                              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide sm:mr-2">
                                Send test to:
                              </label>
                              <input
                                type="email"
                                value={testEmailRecipient}
                                onChange={(e) => setTestEmailRecipient(e.target.value)}
                                placeholder="you@yourdomain.com"
                                data-testid="test-email-recipient-input"
                                className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-600"
                              />
                              <button
                                onClick={handleSendTestEmail}
                                disabled={sendingTestEmail}
                                data-testid="send-test-email-btn"
                                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm font-bold px-4 py-2 rounded-md transition-colors whitespace-nowrap"
                              >
                                {sendingTestEmail ? 'Sending…' : 'Send Test'}
                              </button>
                            </div>
                            <iframe
                              title={`Preview of ${current.name}`}
                              srcDoc={current.html}
                              data-testid="email-preview-frame"
                              sandbox="allow-same-origin"
                              className="w-full border-0"
                              style={{ height: '820px' }}
                            />
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'blog' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Blog Management</h2>
                  <div className="space-x-3">
                    <button
                      onClick={async () => {
                        if (!window.confirm(
                          'Run blog tidy-up?\n\n' +
                          '• Adds a topical image to any post missing one\n' +
                          '• Re-checks duplicate posts are merged and off-topic posts are hidden from Google\n\n' +
                          'Never changes dates and never publishes anything. Safe to run any time.'
                        )) return;
                        try {
                          const token = localStorage.getItem('adminToken');
                          const res = await axios.post(`${API}/admin/blog/migrate`, {},
                            { headers: { Authorization: `Bearer ${token}` }, timeout: 90000 });
                          const r = res.data;
                          alert(
                            `Tidy-up complete.\n\n` +
                            `• Images added: ${r.reimaged}\n` +
                            `• Duplicates merged: ${r.consolidated?.merged ?? 0}\n` +
                            `• Hidden from Google: ${r.consolidated?.noindexed ?? 0}`
                          );
                          fetchDashboardData();
                        } catch (e) {
                          if (e.response?.status === 401) {
                            alert('Your admin session has expired. Please log in again.');
                            localStorage.removeItem('adminToken');
                            window.location.reload();
                            return;
                          }
                          alert('Migration failed: ' + (e.response?.data?.detail || e.message));
                        }
                      }}
                      className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition-colors"
                      data-testid="admin-run-blog-migrations"
                      title="Fill missing images and re-check duplicates. Never changes dates or publishes"
                    >
                      Run Tidy-up
                    </button>
                    <button
                      onClick={fetchDashboardData}
                      className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                    >
                      Refresh
                    </button>
                    <button
                      onClick={() => { setEditingItem(null); setShowBlogForm(true); }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="w-4 h-4 inline mr-2" />
                      New Post
                    </button>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  {blogPosts.length === 0 ? (
                    <div className="text-center py-8">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">No blog posts yet</p>
                      <p className="text-sm text-gray-400">
                        Create your first blog post to start building your content library
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Title
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Tags
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {blogPosts.map((post) => (
                            <tr key={post.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4">
                                <div>
                                  <p className="font-semibold text-gray-900">{post.title}</p>
                                  <p className="text-sm text-gray-500">/{post.slug}</p>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                  post.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {post.published ? 'Published' : 'Draft'}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-wrap gap-1">
                                  {(post.tags || []).slice(0, 3).map((tag, idx) => (
                                    <span key={idx} className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                                      {tag}
                                    </span>
                                  ))}
                                  {post.tags && post.tags.length > 3 && (
                                    <span className="text-xs text-gray-500">+{post.tags.length - 3}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(post.created_at)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => openAiPreview(post.slug)}
                                    className="text-purple-600 hover:text-purple-700"
                                    title="Preview how AI answer engines see this post"
                                    data-testid={`ai-preview-${post.slug}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => { setEditingItem(post); setShowBlogForm(true); }}
                                    className="text-blue-600 hover:text-blue-700"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteBlogPost(post.id || post.slug)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
      
      {/* Details Modal */}
      {showDetailsModal && <DetailsModal />}
      
      {/* Forms */}
      {showBlogForm && (
        <BlogForm
          post={editingItem}
          onSave={saveBlogPost}
          onCancel={() => { setShowBlogForm(false); setEditingItem(null); }}
          loading={formLoading}
        />
      )}
    </div>
  );
};

export default Admin;